/* ==========================================================================
   Hamam Qatar — حمام قطر
   global.js — the theme's only JavaScript file. Vanilla, no dependencies.

   Contract with the Liquid layer:
     • window.HamamTheme (created in layout/theme.liquid) supplies
       routes, strings, settings, moneyFormat, isRtl. This file NEVER
       hard-codes a user-facing string or a URL.
     • Behaviour is attached through native custom elements where a DOM
       subtree owns it, and through delegated document listeners where the
       behaviour is global (drawers, wishlist, quick view).
     • Everything is defensive: a missing element is a no-op, never a throw.
       Sections render in the theme editor in any order, and snippets may not
       exist yet, so every lookup is null-checked.
     • Re-wiring happens on `shopify:section:load` so the editor stays live.
   ========================================================================== */

(function () {
  'use strict';

  /* ========================================================================
     0. Namespace + environment
     ======================================================================== */

  const Theme = (window.HamamTheme = window.HamamTheme || {});

  Theme.routes = Theme.routes || {};
  Theme.strings = Theme.strings || {};
  Theme.settings = Theme.settings || {};
  Theme.moneyFormat = Theme.moneyFormat || '{{amount}}';
  Theme.isRtl = Boolean(Theme.isRtl);

  const routes = Theme.routes;
  const strings = Theme.strings;
  const settings = Theme.settings;

  /** Reduced motion is read live — the user can flip it mid-session. */
  const reducedMotionQuery =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

  const prefersReducedMotion = () => Boolean(reducedMotionQuery && reducedMotionQuery.matches);

  /** Every scroll call in this file goes through here. */
  const scrollBehavior = () => (prefersReducedMotion() ? 'auto' : 'smooth');

  const t = (key, fallback) => {
    const value = strings[key];
    return typeof value === 'string' && value.length ? value : fallback || '';
  };

  /** routes.root is '/' or '/ar' — join without doubling slashes. */
  const rootPath = (path) => {
    const root = (routes.root || '/').replace(/\/+$/, '');
    return root + '/' + String(path || '').replace(/^\/+/, '');
  };

  const $ = (selector, scope) => (scope || document).querySelector(selector);
  const $$ = (selector, scope) => Array.from((scope || document).querySelectorAll(selector));

  const on = (target, type, handler, options) => {
    if (target && typeof target.addEventListener === 'function') {
      target.addEventListener(type, handler, options);
    }
  };

  /* ========================================================================
     1. Utilities
     ======================================================================== */

  /**
   * Push a message into the polite live region declared in theme.liquid.
   * Cleared first so screen readers re-announce an identical message.
   */
  function announce(message) {
    const region = document.getElementById('ThemeLiveRegion');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = String(message);
    }, 60);
  }

  /**
   * Shopify money formatting. Mirrors the Liquid `money` filter so AJAX-built
   * prices match server-rendered ones exactly.
   */
  function formatMoney(cents, format) {
    const template = format || Theme.moneyFormat || '{{amount}}';
    let value = typeof cents === 'string' ? cents.replace(/[^0-9.-]/g, '') : cents;
    value = Number(value);
    if (!isFinite(value)) value = 0;

    const placeholder = /\{\{\s*(\w+)\s*\}\}/;

    const withDelimiters = (number, precision, thousands, decimal) => {
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const integer = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const fraction = parts[1] ? decimal + parts[1] : '';
      return integer + fraction;
    };

    const match = template.match(placeholder);
    const name = match ? match[1] : 'amount';
    let formatted;

    switch (name) {
      case 'amount_no_decimals':
        formatted = withDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = withDelimiters(value, 2, "'", '.');
        break;
      case 'amount':
      default:
        formatted = withDelimiters(value, 2, ',', '.');
        break;
    }

    return template.replace(placeholder, formatted);
  }

  /** Fetch a URL and hand back a parsed Document (Section Rendering API, facets). */
  async function fetchDom(url, options) {
    const response = await fetch(url, Object.assign({ credentials: 'same-origin' }, options || {}));
    if (!response.ok) throw new Error('Request failed: ' + response.status + ' ' + url);
    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function debounce(fn, wait) {
    let timer = null;
    return function debounced(...args) {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn.apply(this, args), wait || 0);
    };
  }

  function throttleRaf(fn) {
    let frame = null;
    return function throttled(...args) {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        fn.apply(this, args);
      });
    };
  }

  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), summary, iframe, object, embed, ' +
    '[tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

  const focusableIn = (container) =>
    container
      ? $$(FOCUSABLE, container).filter((el) => {
          if (el.hasAttribute('hidden') || el.getAttribute('aria-hidden') === 'true') return false;
          /* Ancestors matter too. Inactive mobile-nav panels carry aria-hidden
             on the PANEL, not on their links, and hide with `visibility`, which
             still generates boxes — so offsetParent stays non-null and every
             link in every collapsed sub-panel counted as focusable. The trap's
             "last focusable" was then an untabbable link in a hidden panel, so
             Tab escaped the drawer for one stop. */
          if (el.closest('[hidden], [aria-hidden="true"]')) return false;
          if (typeof window.getComputedStyle === 'function' && window.getComputedStyle(el).visibility === 'hidden') return false;
          // offsetParent is null for display:none subtrees; fixed elements need the rect check.
          return el.offsetParent !== null || el.getClientRects().length > 0;
        })
      : [];

  /* ---- Focus trap ---------------------------------------------------------
     One trap is active at a time, but traps stack (drawer → modal opened from
     inside it). Each entry remembers the element that opened it so focus can
     be handed back exactly where the user left off. A single document-level
     keydown listener services the top of the stack: cheaper than binding and
     unbinding per container, and it cannot leak listeners.
     ---------------------------------------------------------------------- */

  const focusStack = [];

  function handleTrapKeydown(event) {
    if (event.key !== 'Tab' || !focusStack.length) return;
    const trap = focusStack[focusStack.length - 1];
    const items = focusableIn(trap.container);
    if (!items.length) {
      // Nothing tabbable inside — keep focus pinned on the container itself.
      event.preventDefault();
      if (typeof trap.container.focus === 'function') trap.container.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !trap.container.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !trap.container.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  function trapFocus(container, opener, key) {
    if (!container) return;
    if (!focusStack.length) on(document, 'keydown', handleTrapKeydown, true);

    focusStack.push({
      container,
      key: key == null ? null : key,
      opener: opener || (document.activeElement instanceof HTMLElement ? document.activeElement : null)
    });

    const items = focusableIn(container);
    const target = container.querySelector('[data-autofocus]') || items[0];
    if (target) {
      window.requestAnimationFrame(() => {
        try {
          target.focus({ preventScroll: true });
        } catch (_) {
          target.focus();
        }
      });
    } else if (container.getAttribute('tabindex') === null) {
      container.setAttribute('tabindex', '-1');
      container.focus({ preventScroll: true });
    }
  }

  /* Release BY IDENTITY, not by position.

     The stack is LIFO but DrawerController.open is a Set that can be closed in
     any order — and the quick-view modal (--z-modal 300) legitimately sits
     above the cart drawer (--z-drawer 200), so "add to cart from quick view"
     leaves both open. Closing the quick view then popped the CART's entry: the
     stack top became a closed container, and every subsequent Tab was trapped
     inside an invisible modal with the open drawer unreachable. */
  function releaseFocus(key) {
    let index = focusStack.length - 1;
    if (key != null) {
      const found = focusStack.map((t) => t.key).lastIndexOf(key);
      if (found === -1) return;
      index = found;
    }
    if (index < 0) return;
    const wasTop = index === focusStack.length - 1;
    const trap = focusStack.splice(index, 1)[0];
    if (!focusStack.length) document.removeEventListener('keydown', handleTrapKeydown, true);
    if (wasTop && trap && trap.opener && document.contains(trap.opener)) {
      try {
        trap.opener.focus({ preventScroll: true });
      } catch (_) {
        trap.opener.focus();
      }
    }
  }

  /* ---- Scroll lock (reference counted, position preserving) ------------- */

  let scrollLocks = 0;
  let lockedScrollY = 0;

  function lockScroll() {
    scrollLocks += 1;
    if (scrollLocks > 1) return;
    lockedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.classList.add('is-scroll-locked');
    document.body.style.setProperty('--locked-scroll-y', lockedScrollY + 'px');
  }

  function unlockScroll(force) {
    if (force) scrollLocks = 0;
    else scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks > 0) return;
    document.body.classList.remove('is-scroll-locked');
    document.body.style.removeProperty('--locked-scroll-y');
    // Restoring explicitly keeps iOS Safari from jumping to the top.
    window.scrollTo({ top: lockedScrollY, behavior: 'auto' });
  }

  /* ========================================================================
     2. Toast
     ======================================================================== */

  let toastEl = null;
  let toastTimer = null;

  function toast(message) {
    if (!message) return;
    if (!toastEl || !document.body.contains(toastEl)) {
      toastEl = document.querySelector('.toast[data-theme-toast]');
      if (!toastEl) {
        toastEl = document.createElement('div');
        toastEl.className = 'toast';
        toastEl.setAttribute('data-theme-toast', '');
        toastEl.setAttribute('role', 'status');
        toastEl.setAttribute('aria-live', 'polite');
        document.body.appendChild(toastEl);
      }
    }
    toastEl.textContent = String(message);
    toastEl.setAttribute('data-open', '');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      if (toastEl) toastEl.removeAttribute('data-open');
    }, 2800);
  }

  /* ========================================================================
     3. Free-shipping progress bar
     ======================================================================== */

  function updateShipBar(cartTotal) {
    const bars = $$('[data-ship-bar]');
    if (!bars.length) return;

    const threshold = Number(settings.freeShippingThreshold) || 0;
    const total = Number(cartTotal) || 0;

    bars.forEach((bar) => {
      const fill = bar.querySelector('[data-ship-bar-fill]') || bar.querySelector('.ship-bar__fill');
      const text = bar.querySelector('[data-ship-bar-text]') || bar.querySelector('.ship-bar__text');

      if (threshold <= 0) {
        bar.hidden = true;
        return;
      }
      bar.hidden = false;

      const remaining = Math.max(0, threshold - total);
      const percent = Math.max(0, Math.min(100, (total / threshold) * 100));
      const complete = remaining <= 0;

      if (fill) fill.style.width = percent + '%';
      bar.classList.toggle('ship-bar--complete', complete);
      bar.setAttribute('data-progress', String(Math.round(percent)));

      if (text) {
        const label = complete
          ? t('freeDeliveryReached')
          : t('freeDeliveryProgress').replace('[amount]', formatMoney(remaining));
        // Keep any leading icon markup, replace only the text node payload.
        const icon = text.querySelector('svg');
        text.textContent = label;
        if (icon) text.insertBefore(icon, text.firstChild);
      }
    });
  }

  /* ========================================================================
     4. Overlay + drawer controller
     ======================================================================== */

  const DrawerController = {
    open: new Set(),

    overlay() {
      return document.querySelector('[data-overlay]');
    },

    isOpen(id) {
      return this.open.has(id);
    },

    syncOverlay() {
      const overlay = this.overlay();
      if (!overlay) return;
      if (this.open.size) {
        overlay.hidden = false;
        // Force a frame so the CSS transition runs from the hidden state.
        window.requestAnimationFrame(() => overlay.setAttribute('data-open', ''));
      } else {
        overlay.removeAttribute('data-open');
        window.setTimeout(() => {
          if (!this.open.size) overlay.hidden = true;
        }, 300);
      }
    },

    openDrawer(id, opener) {
      if (!id) return;
      const el = document.getElementById(id);
      if (!el || this.open.has(id)) return;

      el.hidden = false;
      el.removeAttribute('aria-hidden');
      // Next frame: `hidden` removal and the transform change must not batch.
      window.requestAnimationFrame(() => {
        el.setAttribute('data-open', '');
        /* Trap AFTER the drawer is painted, not before.

           focusableIn() filters on visibility, and the drawer stays
           `visibility: hidden` until [data-open] lands. Trapping synchronously
           (as this did) found zero focusable children, fell through to the
           tabindex="-1" fallback, and focused a still-hidden container — so
           focus never left <body> and keyboard and screen-reader users were
           stranded behind the overlay. One more frame and the children are
           focusable. */
        window.requestAnimationFrame(() => trapFocus(el, opener, id));
      });

      this.open.add(id);
      /* The mobile nav is rendered INSIDE the header section, which is a
         stacking context (position: sticky). It therefore cannot rise above the
         body-level overlay on its own — the section wrapper has to be lifted
         with it. Deliberately NOT applied to the cart/search drawers: those are
         body-level siblings of the overlay and must stay above the header. */
      if (id === 'MobileNav') document.body.classList.add('has-mobile-nav');
      lockScroll();
      this.syncOverlay();

      $$('[aria-controls="' + id + '"]').forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
      el.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true, detail: { id } }));
    },

    closeDrawer(id) {
      if (!id || !this.open.has(id)) return;
      const el = document.getElementById(id);
      this.open.delete(id);

      if (el) {
        el.removeAttribute('data-open');
        el.setAttribute('aria-hidden', 'true');
        el.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true, detail: { id } }));
      }

      $$('[aria-controls="' + id + '"]').forEach((btn) => btn.setAttribute('aria-expanded', 'false'));

      if (id === 'MobileNav') document.body.classList.remove('has-mobile-nav');

      releaseFocus(id);
      unlockScroll();
      this.syncOverlay();
    },

    closeAll() {
      Array.from(this.open).forEach((id) => this.closeDrawer(id));
    },

    closeTop() {
      const ids = Array.from(this.open);
      if (ids.length) this.closeDrawer(ids[ids.length - 1]);
    }
  };

  /** Map of trigger attribute → target element id. */
  const DRAWER_TRIGGERS = [
    ['[data-cart-trigger]', 'CartDrawer'],
    ['[data-search-trigger]', 'SearchDrawer'],
    ['[data-mobile-nav-trigger]', 'MobileNav']
  ];

  function initDrawers() {
    // Delegated: works for markup injected later by section rendering.
    on(document, 'click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      for (const [selector, id] of DRAWER_TRIGGERS) {
        const trigger = target.closest(selector);
        if (trigger) {
          if (!document.getElementById(id)) return; // snippet absent — let the link work
          event.preventDefault();
          DrawerController.openDrawer(id, trigger);
          return;
        }
      }

      const generic = target.closest('[data-drawer-open]');
      if (generic) {
        const id = generic.getAttribute('data-drawer-open');
        if (id && document.getElementById(id)) {
          event.preventDefault();
          DrawerController.openDrawer(id, generic);
          return;
        }
      }

      const closer = target.closest('[data-drawer-close]');
      if (closer) {
        event.preventDefault();
        const explicit = closer.getAttribute('data-drawer-close');
        const host = closer.closest('[data-open]') || closer.closest('.drawer, .modal');
        const id = explicit || (host && host.id);
        if (id) DrawerController.closeDrawer(id);
        else DrawerController.closeTop();
        return;
      }

      if (target.closest('[data-overlay]')) {
        DrawerController.closeAll();
      }
    });

    on(document, 'keydown', (event) => {
      if (event.key === 'Escape' && DrawerController.open.size) {
        event.stopPropagation();
        DrawerController.closeTop();
      }
    });
  }

  /* ========================================================================
     5. Cart API layer
     ======================================================================== */

  /* Only sections that actually exist and are actually consumed. This used to
     also request `cart-icon-bubble`, which is not a section in this theme and
     was never read from the response. */
  const CART_SECTIONS = 'cart-drawer';

  async function postJson(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    });

    let data = null;
    try {
      data = await response.json();
    } catch (_) {
      data = null;
    }

    if (!response.ok) {
      const serverMessage = data && (data.description || data.message || data.error);
      const message = typeof serverMessage === 'string' && serverMessage.trim()
        ? serverMessage
        : response.status === 422
          ? t('unavailable', 'Unavailable')
          : 'Request failed (' + response.status + ')';
      const error = new Error(message);
      error.data = data;
      error.status = response.status;
      throw error;
    }
    return data;
  }

  /* Remembered between calls so we can tell a genuine increase from the
     initial page-load read — the cart icon should not pulse just because the
     page finished booting with items already in the basket. */
  let lastKnownItemCount = null;

  /**
   * One beat on the cart icon. This is the fallback confirmation channel: when
   * the drawer is disabled in theme settings, an add otherwise produces no
   * visible change anywhere near the pointer, and users re-click.
   */
  function bumpCartIcon() {
    if (prefersReducedMotion() || settings.animationsEnabled === false) return;
    $$('[data-cart-icon]').forEach((el) => {
      el.classList.remove('is-bumped');
      // Force a reflow so the class re-application restarts the animation on
      // rapid successive adds instead of being coalesced away.
      void el.offsetWidth;
      el.classList.add('is-bumped');
      window.setTimeout(() => el.classList.remove('is-bumped'), 500);
    });
  }

  /**
   * Units of a variant already in the cart. Prefers the fresh cart carried by a
   * cart:updated event: that event fires before HamamTheme.cartItems is
   * refreshed, so reading the global there sees the cart from before the add.
   */
  function cartQtyFor(variantId, freshCart) {
    const items = freshCart && freshCart.items
      ? freshCart.items.map((item) => ({ id: item.variant_id, quantity: item.quantity }))
      : (window.HamamTheme && window.HamamTheme.cartItems) || [];
    return items
      .filter((item) => Number(item.id) === Number(variantId))
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  }

  /**
   * Units that can still be added for a stock-limited variant (data-stock-limit
   * / data-stock-variant, rendered only for tracked + "stop selling" variants),
   * or Infinity when there is no limit or the element describes another variant.
   */
  function remainingStock(el, variantId, freshCart) {
    if (!el || !el.dataset || el.dataset.stockLimit == null || el.dataset.stockLimit === '') return Infinity;
    if (variantId != null && Number(el.dataset.stockVariant) !== Number(variantId)) return Infinity;
    return Math.max(0, Number(el.dataset.stockLimit) - cartQtyFor(el.dataset.stockVariant, freshCart));
  }

  function syncCardStockLabel(card, freshCart) {
    if (!card || !card.dataset || card.dataset.stockLimit == null) return;
    const stock = card.querySelector('[data-card-stock]');
    if (!stock) return;
    const limit = Number(card.dataset.stockLimit);
    const inCart = cartQtyFor(card.dataset.stockVariant, freshCart);
    const remaining = Math.max(0, limit - inCart);
    const low = remaining <= 5;
    stock.classList.toggle('pcard__stock--low', low);
    stock.classList.toggle('pcard__stock--in', !low);
    const dot = stock.querySelector('.pcard__stock-dot');
    if (dot) dot.hidden = low;
    const label = stock.querySelector('[data-stock-text]');
    if (label) {
      label.textContent = inCart > 0
        ? remaining > 0
          ? t('remainingToAdd', '').replace('[count]', String(remaining))
          : t('allInCartShort', '')
        : low
          ? t('lowStock', '').replace('[count]', String(remaining))
          : t('inStock', '');
    }
  }

  function syncCardStockLabels(freshCart) {
    $$('[data-product-card][data-stock-limit]').forEach((card) => syncCardStockLabel(card, freshCart));
  }

  function updateCartCounts(cart) {
    if (!cart) return;
    if (cart.items && window.HamamTheme) {
      window.HamamTheme.cartItems = cart.items.map(item => ({ id: item.variant_id, quantity: item.quantity }));
    }
    const count = Number(cart.item_count) || 0;
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count === 0;
      el.toggleAttribute('data-empty', count === 0);
    });
    if (lastKnownItemCount !== null && count > lastKnownItemCount) bumpCartIcon();
    lastKnownItemCount = count;
    updateShipBar(cart.total_price);
  }

  const cart = {
    /** Read the current cart as JSON. */
    async get() {
      const response = await fetch(routes.cart + '.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Could not read cart');
      return response.json();
    },

    /**
     * @param {FormData|Object|Array} payload FormData from a product form, a
     *        single {id, quantity, properties} object, or an items array.
     *
     * FormData carrying repeated `items[][id]` / `items[][quantity]` fields (the
     * bundle form in sections/frequently-bought.liquid) is collected into an
     * `items` array: a new entry starts at every `items[][id]`, and the other
     * `items[][…]` fields attach to the entry being built.
     */
    async add(payload) {
      let body;

      if (typeof FormData !== 'undefined' && payload instanceof FormData) {
        const single = {};
        const bundle = [];

        payload.forEach((value, key) => {
          const bundleField = /^items\[\]\[([^\]]+)\]$/.exec(key);
          if (bundleField) {
            const field = bundleField[1];
            // `id` opens a new line; anything arriving before one is still kept.
            if (field === 'id' || !bundle.length) bundle.push({});
            const item = bundle[bundle.length - 1];
            if (field === 'quantity') item.quantity = Number(value) || 1;
            else if (/^properties\[/.test(field)) {
              item.properties = item.properties || {};
              item.properties[field.slice(11, -1)] = value;
            } else item[field] = value;
            return;
          }

          if (key === 'quantity') single.quantity = Number(value) || 1;
          else if (key === 'id' || key === 'selling_plan') single[key] = value;
          else if (/^properties\[/.test(key)) {
            single.properties = single.properties || {};
            single.properties[key.slice(11, -1)] = value;
          }
        });

        if (bundle.length) {
          const items = bundle
            .filter((item) => item.id)
            .map((item) => Object.assign({ quantity: 1 }, item));
          if (!items.length) throw new Error(t('unavailable', 'Unavailable'));
          body = { items, sections: CART_SECTIONS };
        } else {
          if (!single.id) throw new Error(t('unavailable', 'Unavailable'));
          body = { items: [single], sections: CART_SECTIONS };
        }
      } else if (Array.isArray(payload)) {
        body = { items: payload, sections: CART_SECTIONS };
      } else if (payload && payload.items) {
        body = Object.assign({ sections: CART_SECTIONS }, payload);
      } else {
        body = { items: [payload], sections: CART_SECTIONS };
      }

      const result = await postJson(routes.cartAdd, body);
      const fresh = await this.get().catch(() => null);

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { cart: fresh, sections: result && result.sections, source: 'add' }
        })
      );

      /*
      if (settings.cartType === 'drawer' && document.getElementById('CartDrawer')) {
        DrawerController.openDrawer('CartDrawer');
      }
      */
      toast(t('added', 'Added to cart'));
      announce(t('added', 'Added to cart'));

      return result;
    },

    async change(options) {
      const opts = options || {};
      const body = { sections: CART_SECTIONS };
      if (opts.line != null) body.line = opts.line;
      else if (opts.id != null) body.id = opts.id;
      if (opts.quantity != null) body.quantity = opts.quantity;
      if (opts.properties) body.properties = opts.properties;

      const result = await postJson(routes.cartChange, body);

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { cart: result, sections: result && result.sections, source: 'change' }
        })
      );
      return result;
    },

    async updateNote(note) {
      const result = await postJson(routes.cartUpdate || routes.cart + '/update.js', {
        note: note == null ? '' : String(note),
        sections: CART_SECTIONS
      });
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { cart: result, sections: result && result.sections, source: 'note' }
        })
      );
      return result;
    },

    async updateAttributes(attributes) {
      const result = await postJson(routes.cartUpdate || routes.cart + '/update.js', {
        attributes: attributes || {},
        sections: CART_SECTIONS
      });
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { cart: result, sections: result && result.sections, source: 'attributes' }
        })
      );
      return result;
    },

    /**
     * Discount codes cannot be applied through the AJAX Cart API — Shopify
     * only accepts them at checkout. The /discount/CODE route stores the code
     * in the session and redirects, which is the supported workaround.
     */
    applyDiscount(code) {
      const clean = String(code || '').trim();
      if (!clean) return;
      window.location = rootPath('discount/' + encodeURIComponent(clean)) + '?redirect=' + encodeURIComponent(routes.cart);
    }
  };

  /* ========================================================================
     6. <sticky-header>
     ======================================================================== */

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = window.scrollY || 0;
      this.onScroll = throttleRaf(this.update.bind(this));
      this.onResize = debounce(this.measure.bind(this), 150);

      this.measure();
      this.update();
      on(window, 'scroll', this.onScroll, { passive: true });
      on(window, 'resize', this.onResize);

      /* Dismissing the announcement bar changes how much chrome sits above the
         page without resizing the header itself, so the ResizeObserver below
         never fires for it. */
      this.onAnnouncementDismissed = () => this.measure();
      on(document, 'announcement:dismissed', this.onAnnouncementDismissed);

      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.measure());
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('announcement:dismissed', this.onAnnouncementDismissed);
      if (this.observer) this.observer.disconnect();
    }

    measure() {
      const root = document.documentElement.style;

      /* --header-h is the STICKY BAR alone. Every sticky sidebar in the theme
         (collection facets, product info, cart summary, pre-booking support)
         offsets against it, and those all sit far down the page where the
         announcement bar is long gone — folding the bar's height in here would
         push all of them down by 40px forever. */
      const height = this.offsetHeight;
      if (height > 0) root.setProperty('--header-h', height + 'px');

      /* --header-total-h includes whatever chrome sits above the header and is
         only meaningful at the top of the page, where the transparent-over-hero
         overlap is measured. */
      const group = this.closest('.shopify-section--header');
      const bar = document.querySelector('.shopify-section--announcement-bar');
      const above = bar && !bar.hidden && group ? bar.offsetHeight : 0;
      if (height > 0) root.setProperty('--header-total-h', height + above + 'px');
    }

    /** True while anything anchored to the bar would be torn off-screen with it. */
    hasOpenMenu() {
      return !!this.querySelector('[data-mega-panel][data-open]');
    }

    update() {
      const y = Math.max(0, window.scrollY || 0);
      this.classList.toggle('is-stuck', y > 20);

      if (this.hasAttribute('data-no-hide')) {
        this.lastY = y;
        return;
      }

      const scrollingDown = y > this.lastY + 4;
      const scrollingUp = y < this.lastY - 4;

      /* The mega panel is positioned against .hdr__bar, so hiding the bar drags
         an open dropdown off the top of the screen mid-interaction — the menu
         appeared to vanish while the shopper was reading it. Stay put until it
         closes. */
      if (this.hasOpenMenu()) {
        this.classList.remove('is-hidden');
        this.lastY = y;
        return;
      }

      if (scrollingDown && y > 300) this.classList.add('is-hidden');
      else if (scrollingUp || y <= 300) this.classList.remove('is-hidden');

      if (scrollingDown || scrollingUp) this.lastY = y;
    }
  }

  /* ========================================================================
     7. <mega-menu>
     ======================================================================== */

  class MegaMenu extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[aria-expanded]');
      this.panel =
        this.querySelector('[data-mega-panel]') ||
        (this.button && this.button.getAttribute('aria-controls')
          ? document.getElementById(this.button.getAttribute('aria-controls'))
          : null);

      if (!this.button || !this.panel) return;

      this.closeTimer = null;
      this.hoverable = window.matchMedia
        ? window.matchMedia('(hover: hover) and (pointer: fine)').matches
        : false;

      this.onButtonClick = (event) => {
        event.preventDefault();
        this.toggle(!this.isOpen());
      };
      this.onKeydown = (event) => {
        if (event.key === 'Escape' && this.isOpen()) {
          event.stopPropagation();
          this.toggle(false);
          /* Guard the focus round-trip: returning focus to the trigger fires
             focusin on this element, which would otherwise re-open what Escape
             just closed. */
          this.suppressFocusOpen = true;
          this.button.focus();
          window.requestAnimationFrame(() => {
            this.suppressFocusOpen = false;
          });
        }
        if (event.key === 'ArrowDown' && event.target === this.button) {
          event.preventDefault();
          this.toggle(true);
          const first = focusableIn(this.panel)[0];
          if (first) first.focus();
        }
      };
      /* Focus opens the panel only when focus arrives from OUTSIDE the menu —
         i.e. someone tabbing into it. Re-focusing the trigger from within (which
         is exactly what Escape does) must not count, or Escape closes the panel
         and the returning focus immediately re-opens it. That was the old
         `:focus-within` bug, and it is just as easy to reintroduce here. */
      this.onFocusIn = (event) => {
        window.clearTimeout(this.closeTimer);
        if (this.suppressFocusOpen) return;
        if (this.contains(event.relatedTarget)) return;
        this.toggle(true);
      };
      this.onFocusOut = (event) => {
        if (!this.contains(event.relatedTarget)) this.toggle(false);
      };
      this.onEnter = () => {
        if (!this.hoverable) return;
        window.clearTimeout(this.closeTimer);
        this.toggle(true);
      };
      this.onLeave = () => {
        if (!this.hoverable) return;
        // Small grace period so a diagonal cursor path to the panel survives.
        window.clearTimeout(this.closeTimer);
        this.closeTimer = window.setTimeout(() => this.toggle(false), 120);
      };

      /* A tap outside the bar has to close an open panel. Without this the only
         way to dismiss a panel opened by tapping its trigger on a touch device
         was to tap that exact trigger again. */
      this.onDocumentPointerDown = (event) => {
        if (!this.isOpen()) return;
        if (event.target instanceof Node && this.contains(event.target)) return;
        this.toggle(false);
      };

      on(this.button, 'click', this.onButtonClick);
      on(this, 'keydown', this.onKeydown);
      on(this, 'focusin', this.onFocusIn);
      on(this, 'focusout', this.onFocusOut);
      on(this, 'pointerenter', this.onEnter);
      on(this, 'pointerleave', this.onLeave);
      on(document, 'pointerdown', this.onDocumentPointerDown);
    }

    disconnectedCallback() {
      window.clearTimeout(this.closeTimer);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    }

    isOpen() {
      return this.button && this.button.getAttribute('aria-expanded') === 'true';
    }

    toggle(open) {
      if (!this.button || !this.panel) return;
      if (this.isOpen() === open) return;

      /* Only one panel at a time. Two open full-width panels stack on top of
         each other and the shopper reads whichever happens to paint last. */
      if (open) {
        $$('mega-menu').forEach((other) => {
          if (other !== this && typeof other.toggle === 'function') other.toggle(false);
        });
      }

      this.button.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.classList.toggle('is-open', open);
      this.panel.toggleAttribute('data-open', open);
    }
  }

  /* ========================================================================
     8. <mobile-nav>
     ======================================================================== */

  class MobileNav extends HTMLElement {
    connectedCallback() {
      this.stack = [];
      this.root = this.querySelector('[data-nav-root]') || this.querySelector('[data-nav-panel]');
      /* Remembers which row opened which panel so Back can return focus to the
         row the shopper actually pressed, rather than dumping them at the top
         of the parent list with no idea where they came from. */
      this.openers = new WeakMap();

      this.onClick = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const push = target.closest('[data-nav-push]');
        if (push && this.contains(push)) {
          event.preventDefault();
          this.push(push.getAttribute('data-target') || push.getAttribute('data-nav-push'), push);
          return;
        }

        const back = target.closest('[data-nav-back]');
        if (back && this.contains(back)) {
          event.preventDefault();
          this.pop();
        }
      };

      /* Back also answers to the Escape key and to a left/right swipe-less
         keyboard step, but only while nested — at the root, Escape belongs to
         the drawer controller, which closes the whole panel. */
      this.onKeydown = (event) => {
        if (event.key === 'Escape' && this.stack.length) {
          event.stopPropagation();
          this.pop();
        }
      };

      on(this, 'click', this.onClick);
      on(this, 'keydown', this.onKeydown);
      on(this, 'drawer:close', () => this.reset());
      /* The drawer itself is the scroll container now, and .mnav__panels gets
         an explicit height matching the active panel. Measure once upgrades
         run, again the moment the drawer is actually painted (it is display:
         none before that, so any earlier measurement reads zero), whenever the
         viewport changes, and after webfonts swap in and reflow the rows. */
      on(this, 'drawer:open', () => window.requestAnimationFrame(() => this.syncHeight()));
      on(window, 'resize', () => this.syncHeight());
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.syncHeight());
      this.reset();
    }

    /* Keeps .mnav__panels exactly as tall as the visible panel's content, so
       the utility footer sits right below the menu and the WHOLE drawer
       scrolls — instead of a small inner strip scrolling between a pinned
       header and footer. Panels are absolutely positioned, so without this
       the container would collapse to zero. */
    syncHeight() {
      if (!this.panelsBox) this.panelsBox = this.querySelector('.mnav__panels');
      const panel = this.current();
      if (!this.panelsBox || !panel) return;
      this.panelsBox.style.height = Math.ceil(panel.scrollHeight) + 'px';
    }

    panels() {
      return $$('[data-nav-panel]', this);
    }

    setVisible(panel, visible) {
      if (!panel) return;
      panel.classList.toggle('is-active', visible);
      panel.toggleAttribute('data-open', visible);
      if (visible) panel.removeAttribute('aria-hidden');
      else panel.setAttribute('aria-hidden', 'true');
    }

    current() {
      return this.stack.length ? this.stack[this.stack.length - 1] : this.root;
    }

    push(id, opener) {
      if (!id) return;
      const escape = window.CSS && typeof window.CSS.escape === 'function' ? window.CSS.escape : (v) => v;
      const panel = this.querySelector('#' + escape(id)) || document.getElementById(id);
      /* A malformed data-nav-push value must never activate an element outside
         this drawer. Besides preventing an invalid menu link from changing an
         unrelated component, this keeps the focus transition contained. */
      if (!panel || !this.contains(panel) || !panel.matches('[data-nav-panel]')) return;

      const from = this.current();
      if (panel === from) return;

      this.stack.push(panel);
      if (opener) {
        this.openers.set(panel, opener);
        opener.setAttribute('aria-expanded', 'true');
      }
      this.setVisible(from, false);
      this.setVisible(panel, true);
      this.classList.add('is-nested');
      this.focusPanel(panel);
      this.syncHeight();
      /* A new level starts at its own top — inheriting the parent's scroll
         offset would land the shopper mid-list under the Back button. */
      this.scrollTop = 0;
    }

    pop() {
      /* Nothing to pop at the root. Without this guard the stack underflowed:
         `leaving` came back undefined, current() fell through to the root, and
         the root was re-shown and re-focused on every stray Back press. */
      if (!this.stack.length) return;

      const leaving = this.stack.pop();
      this.setVisible(leaving, false);

      const opener = leaving && this.openers.get(leaving);
      if (opener) opener.setAttribute('aria-expanded', 'false');

      const next = this.current();
      this.setVisible(next, true);
      this.classList.toggle('is-nested', this.stack.length > 0);

      /* Return the shopper to the row they opened, not to the top of the list. */
      if (opener && next && next.contains(opener)) {
        window.requestAnimationFrame(() => opener.focus({ preventScroll: true }));
      } else {
        this.focusPanel(next);
      }
      this.syncHeight();
    }

    focusPanel(panel) {
      if (!panel) return;
      const heading = panel.querySelector('[data-nav-heading], h2, h3');
      const target = heading || focusableIn(panel)[0];
      if (!target) return;
      if (target === heading && heading.getAttribute('tabindex') === null) {
        heading.setAttribute('tabindex', '-1');
      }
      window.requestAnimationFrame(() => target.focus({ preventScroll: true }));
    }

    reset() {
      this.stack = [];
      this.classList.remove('is-nested');

      /* Re-resolve the root every time. It is looked up once in
         connectedCallback, but the header section can be re-rendered by the
         Theme Editor underneath us — and if `this.root` is ever stale or null,
         the line below would deactivate EVERY panel and leave the shopper
         staring at an empty drawer. Falling back to the first panel guarantees
         something is always visible. */
      const panels = this.panels();
      if (!this.root || !this.contains(this.root)) {
        this.root = this.querySelector('[data-nav-root]') || panels[0] || null;
      }
      const root = this.root || panels[0];

      panels.forEach((panel) => this.setVisible(panel, panel === root));
      $$('[data-nav-push]', this).forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      this.syncHeight();
    }
  }

  /* ========================================================================
     9. <localization-form>
     ======================================================================== */

  /* ==========================================================================
     Colour-scheme switch

     Owns exactly one piece of state: the visitor's explicit choice, stored
     under 'hq-theme'. Everything visual is derived from the `data-theme`
     attribute on <html>, which the inline resolver in theme.liquid has already
     stamped before first paint — this element never sets the initial scheme,
     it only reads it and takes over on interaction.

     Three states, two of them visible:
       'light' / 'dark'  an explicit choice; wins over the OS
       absent            follow the OS, handled entirely in CSS by the
                         prefers-color-scheme block in css-variables

     Following the OS is the default, so the element listens to the media query
     and re-syncs the switch when the system flips mid-session (scheduled dark
     mode at sunset). Once the visitor presses the switch we stop caring about
     that, because they have opted out.
     ========================================================================== */
  class ThemeToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-theme-toggle]');
      if (!this.button) return;

      this.stateEl = this.querySelector('[data-theme-state]');
      this.root = document.documentElement;
      this.media = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;

      this.sync();

      on(this.button, 'click', () => this.toggle());

      /* Keep every instance of the switch in agreement. The desktop header and
         the mobile drawer both render one, and they are separate elements —
         without this, toggling in the drawer would leave the header switch
         showing the old state after the drawer closes. */
      this.onThemeChange = () => this.sync();
      on(document, 'theme:schemechange', this.onThemeChange);

      /* Only relevant while no explicit choice is stored. addEventListener on
         a MediaQueryList is unsupported in older Safari, hence the fallback. */
      this.onMediaChange = () => {
        if (!this.stored()) this.sync();
      };
      if (this.media) {
        if (this.media.addEventListener) this.media.addEventListener('change', this.onMediaChange);
        else if (this.media.addListener) this.media.addListener(this.onMediaChange);
      }
    }

    disconnectedCallback() {
      if (this.onThemeChange) document.removeEventListener('theme:schemechange', this.onThemeChange);
      if (this.media && this.onMediaChange) {
        if (this.media.removeEventListener) this.media.removeEventListener('change', this.onMediaChange);
        else if (this.media.removeListener) this.media.removeListener(this.onMediaChange);
      }
    }

    /* localStorage throws outright in Safari private mode and in some in-app
       webviews, so every access is guarded. A visitor whose storage is blocked
       still gets a working toggle — it just does not survive navigation. */
    stored() {
      try {
        const value = window.localStorage.getItem('hq-theme');
        return value === 'light' || value === 'dark' ? value : null;
      } catch (error) {
        return null;
      }
    }

    save(value) {
      try {
        window.localStorage.setItem('hq-theme', value);
      } catch (error) {
        /* Non-fatal: the choice applies to this page view only. */
      }
    }

    /** The scheme actually in force, explicit choice or OS. */
    current() {
      const attr = this.root.getAttribute('data-theme');
      if (attr === 'light' || attr === 'dark') return attr;
      return this.media && this.media.matches ? 'dark' : 'light';
    }

    /** Push the live scheme into the control, without touching the document. */
    sync() {
      const isDark = this.current() === 'dark';
      const strings = (window.HamamTheme && window.HamamTheme.strings) || {};
      this.button.setAttribute('aria-checked', isDark ? 'true' : 'false');
      if (this.stateEl) {
        this.stateEl.textContent = isDark
          ? strings.appearanceDark || 'Dark'
          : strings.appearanceLight || 'Light';
      }
    }

    toggle() {
      const next = this.current() === 'dark' ? 'light' : 'dark';

      this.root.setAttribute('data-theme', next);
      this.save(next);

      /* Browser chrome (address bar, task switcher) follows the page. The meta
         carries both values so no colour is hard-coded in JS. */
      const meta = document.querySelector('meta[data-theme-color]');
      if (meta) {
        const value = meta.getAttribute(next === 'dark' ? 'data-color-dark' : 'data-color-light');
        if (value) meta.setAttribute('content', value);
      }

      document.dispatchEvent(new CustomEvent('theme:schemechange', { detail: { scheme: next } }));

      const strings = (window.HamamTheme && window.HamamTheme.strings) || {};
      const label = next === 'dark' ? strings.appearanceDark : strings.appearanceLight;
      if (strings.appearanceChanged && label) {
        announce(strings.appearanceChanged.replace('[mode]', label));
      }
    }
  }

  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      on(this, 'change', (event) => {
        const select = event.target;
        if (!(select instanceof HTMLSelectElement)) return;
        // Progressive enhancement: the form still submits via its button
        // when JS is off, so we only save the extra click here.
        this.form.submit();
      });

      /* ---- Dropdown state ---------------------------------------------------
         The trigger shipped a hard-coded aria-expanded="false" that nothing ever
         updated, while the panel opened purely on CSS :hover / :focus-within.
         Two problems, one of them costly:

           - The announced state was permanently wrong.
           - The panel is a list of SUBMIT buttons. On a touch screen the tap
             that "hovered" the trigger could fall straight through onto a
             language button and switch the shopper's language by accident.

         The panel is now explicitly toggled, and the CSS opens on the trigger's
         real aria-expanded. */
      this.toggleButton = this.querySelector('[data-localization-toggle]');
      if (!this.toggleButton) return;

      this.onToggleClick = (event) => {
        event.preventDefault();
        this.setExpanded(this.toggleButton.getAttribute('aria-expanded') !== 'true');
      };

      this.onDocumentPointerDown = (event) => {
        if (this.toggleButton.getAttribute('aria-expanded') !== 'true') return;
        if (event.target instanceof Node && this.contains(event.target)) return;
        this.setExpanded(false);
      };

      this.onKeydown = (event) => {
        if (event.key !== 'Escape') return;
        if (this.toggleButton.getAttribute('aria-expanded') !== 'true') return;
        event.stopPropagation();
        this.setExpanded(false);
        this.toggleButton.focus();
      };

      /* Tabbing out of the dropdown should close it, or a keyboard user leaves
         an open panel floating over the page behind them. */
      this.onFocusOut = (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.setExpanded(false);
      };

      on(this.toggleButton, 'click', this.onToggleClick);
      on(this, 'keydown', this.onKeydown);
      on(this, 'focusout', this.onFocusOut);
      on(document, 'pointerdown', this.onDocumentPointerDown);
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    }

    setExpanded(open) {
      if (!this.toggleButton) return;
      this.toggleButton.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  }

  /* ========================================================================
     10. <cart-drawer>
     ======================================================================== */

  /**
   * Cart drawer recommendations.
   *
   * The drawer ships an empty `[data-cart-recommendations][data-url]` shell —
   * the products come from a separate request (the recommendations route can't
   * be resolved while rendering the cart). Failures are silent: the shell simply
   * stays empty, which is a valid state.
   */
  async function fetchCartRecommendations(scope) {
    const hosts = $$('[data-cart-recommendations][data-url]', scope || document);
    if (!hosts.length) return;

    await Promise.all(
      hosts.map(async (host) => {
        const url = host.getAttribute('data-url');
        if (!url) return;
        if (host.dataset.recsLoaded === 'true') return;
        // Already server-rendered (or filled by a previous pass) — leave it.
        if (host.innerHTML.trim().length) {
          host.dataset.recsLoaded = 'true';
          return;
        }
        host.dataset.recsLoaded = 'true';

        try {
          const doc = await fetchDom(url);
          const source = doc.querySelector('[data-cart-recommendations]') || doc.body;
          const html = source ? source.innerHTML : '';
          if (html.trim().length) host.innerHTML = html;
          else host.removeAttribute('data-recs-loaded');
        } catch (_) {
          // Retry on the next drawer refresh rather than latching the failure.
          host.removeAttribute('data-recs-loaded');
        }
      })
    );
  }

  /**
   * Sources that must never trigger a DOM swap: they originate from a field the
   * customer is actively typing in (the order note, cart attributes). Replacing
   * the drawer's innerHTML mid-keystroke steals focus and drops characters.
   */
  const PASSIVE_CART_SOURCES = ['note', 'attributes'];

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.onCartUpdated = (event) => {
        const detail = event && event.detail;
        const source = detail && detail.source;
        if (source && PASSIVE_CART_SOURCES.indexOf(source) !== -1) {
          // Totals and the bubble still track; the markup is left untouched.
          if (detail.cart) updateCartCounts(detail.cart);
          return;
        }
        this.refresh(detail);
      };
      on(document, 'cart:updated', this.onCartUpdated);
      fetchCartRecommendations(this);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.onCartUpdated);
    }

    inner() {
      return this.querySelector('[data-cart-drawer-inner]');
    }

    async refresh(detail) {
      const cartData = detail && detail.cart;
      if (cartData) updateCartCounts(cartData);

      // Prefer markup Shopify already returned with the cart mutation.
      const sections = detail && detail.sections;
      if (sections && typeof sections['cart-drawer'] === 'string') {
        if (this.swapFrom(sections['cart-drawer'])) {
          fetchCartRecommendations(this);
          return;
        }
      }

      try {
        const doc = await fetchDom(routes.cart + '?section_id=cart-drawer');
        const source = doc.querySelector('[data-cart-drawer-inner]');
        const inner = this.inner();
        if (source && inner) {
          inner.innerHTML = source.innerHTML;
          this.settleFocus();
        } else if (doc.querySelector('cart-drawer')) {
          // Section markup exists but without the inner hook — replace wholesale.
          const replacement = doc.querySelector('cart-drawer');
          this.innerHTML = replacement.innerHTML;
          this.settleFocus();
        }
      } catch (_) {
        /* Offline or the section does not exist: leave the current DOM alone. */
      }

      // The swapped-in markup carries a fresh, empty recommendations shell.
      fetchCartRecommendations(this);

      if (!cartData) {
        cart
          .get()
          .then(updateCartCounts)
          .catch(() => {});
      }
    }

    /** Swap using an HTML string from the `sections` response key. */
    swapFrom(html) {
      const inner = this.inner();
      if (!inner) return false;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source = doc.querySelector('[data-cart-drawer-inner]');
      if (!source) return false;
      inner.innerHTML = source.innerHTML;
      this.settleFocus();
      return true;
    }

    /* Add-to-cart is a three-way race: the submit button is disabled while the
       request is in flight (which drops focus to <body>), openDrawer traps
       focus a frame after paint, and the cart section swaps in around the same
       moment. The trap itself survives — Tab still cycles — but its initial
       placement lands on a node that is being replaced, so focus silently
       stays on <body> and the drawer opens behind the keyboard user.

       Re-place focus once the swapped markup is in, but only when the drawer
       is actually open and focus is not already inside it. Never steal focus
       from a user who has already tabbed somewhere. */
    settleFocus(attempt) {
      const tries = attempt || 0;
      if (tries > 6) return; // ~500ms ceiling, then leave it alone

      window.requestAnimationFrame(() => {
        /* The swap can land a frame or two BEFORE openDrawer sets [data-open].
           Don't give up in that window — wait and look again. */
        if (!this.hasAttribute('data-open')) {
          window.setTimeout(() => this.settleFocus(tries + 1), 60);
          return;
        }
        // Already inside — either the trap won the race or the user has tabbed.
        if (this.contains(document.activeElement)) return;

        const target =
          this.querySelector('[data-autofocus]') ||
          this.querySelector('[data-drawer-close]') ||
          this.querySelector(
            'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled])'
          );
        if (!target) return;

        try {
          target.focus({ preventScroll: true });
        } catch (_) {
          target.focus();
        }

        /* Confirm it stuck. A late recommendations fetch can replace the node
           we just focused, which silently returns focus to <body>. */
        window.setTimeout(() => {
          if (this.hasAttribute('data-open') && !this.contains(document.activeElement)) {
            this.settleFocus(tries + 1);
          }
        }, 80);
      });
    }
  }

  /* ========================================================================
     11. <product-form> / <product-form-quick>
     ======================================================================== */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.button =
        this.querySelector('[data-add-button]') || this.querySelector('button[type="submit"]');
      this.textEl = this.querySelector('[data-add-button-text]') || this.button;
      this.errorEl = this.querySelector('[data-form-error]');
      this.addLabel = this.textEl ? this.textEl.textContent : '';

      on(this.form, 'submit', this.onSubmit.bind(this));

      /* Keep the stepper inside the stock that is still addable. The max
         rendered at page load goes stale after every add, and single-variant
         products have no <variant-picker> to refresh it — which is how a second
         add could ask for more than exists and fail with a 422. */
      this.onCartUpdate = (event) => this.syncStock(event && event.detail && event.detail.cart);
      on(document, 'cart:updated', this.onCartUpdate);
      this.syncStock(null, { quiet: true });
    }

    qtyInput() {
      // getAttribute, not form.id: the form holds an <input name="id">, and
      // named-element access makes `form.id` return that input instead.
      const formId = this.form.getAttribute('id');
      return (
        (formId && document.querySelector('[name="quantity"][form="' + formId + '"]')) ||
        this.form.querySelector('[name="quantity"]')
      );
    }

    currentVariantId() {
      const idInput = this.form.querySelector('[name="id"]');
      return idInput ? idInput.value : null;
    }

    /**
     * Clamp the quantity to what is still addable and, when nothing is left,
     * block the add with a plain explanation instead of letting it fail.
     */
    syncStock(freshCart, options) {
      const input = this.qtyInput();
      if (!input) return;
      const remaining = remainingStock(input, this.currentVariantId(), freshCart);
      if (!isFinite(remaining)) return;
      const dynamic = (this.closest('.mprod__block') || document).querySelector('.mprod__dynamic');

      if (remaining <= 0) {
        this.stockBlocked = true;
        input.max = '1';
        input.value = '1';
        input.disabled = true;
        if (this.button) {
          this.button.disabled = true;
          this.button.setAttribute('aria-disabled', 'true');
        }
        if (this.textEl) this.textEl.textContent = t('allInCartShort', t('maxInCart', ''));
        if (dynamic) dynamic.hidden = true;
        this.showError(t('maxInCart', ''), options && options.quiet);
      } else {
        input.disabled = false;
        input.max = String(remaining);
        if ((parseInt(input.value, 10) || 1) > remaining) input.value = String(remaining);
        if (this.stockBlocked) {
          this.stockBlocked = false;
          if (this.button && this.button.dataset.soldOut !== 'true') {
            this.button.disabled = false;
            this.button.removeAttribute('aria-disabled');
          }
          if (dynamic) dynamic.hidden = false;
          this.showError('');
          if (this.textEl) this.textEl.textContent = this.addLabel || t('addToCart', 'Add to cart');
        }
      }
      const invEl = this.closest('.mprod')?.querySelector('[data-inventory][data-inventory-limited]');
      if (invEl && Number(invEl.dataset.inventoryQuantity) === Number(input.dataset.stockLimit)) {
        const inCart = cartQtyFor(this.currentVariantId(), freshCart);
        const threshold = Number(invEl.dataset.lowStockThreshold) || 5;
        const state = remaining <= 0 ? 'sold_out' : remaining <= threshold ? 'low_stock' : 'in_stock';
        STOCK_STATES.forEach((name) => invEl.classList.toggle('mprod__stock--' + name, name === state));
        const label = invEl.querySelector('[data-inventory-text]');
        if (label) {
          label.textContent = inCart > 0
            ? remaining > 0
              ? t('remainingToAdd', '').replace('[count]', String(remaining))
              : t('maxInCart', '')
            : state === 'low_stock'
              ? t('lowStock', '').replace('[count]', String(remaining))
              : invEl.dataset.showCount !== 'false'
                ? t('inStockCount', '').replace('[count]', String(remaining))
                : t('inStock', '');
        }
      }
      const stepper = input.closest('quantity-input');
      if (stepper && typeof stepper.syncButtons === 'function' && stepper.buttons) stepper.syncButtons();
      // On page load <quantity-input> may not be upgraded yet, so set the
      // blocked state on its buttons directly as well.
      if (remaining <= 0 && stepper) stepper.querySelectorAll('.qty__btn').forEach((btn) => { btn.disabled = true; });
    }

    setBusy(busy, label) {
      if (!this.button) return;
      this.button.classList.toggle('is-loading', busy);
      if (busy) {
        this.button.setAttribute('aria-disabled', 'true');
        this.button.disabled = true;
      } else {
        if (this.button.dataset.soldOut === 'true' || this.stockBlocked) {
          this.button.setAttribute('aria-disabled', 'true');
          this.button.disabled = true;
        } else {
          this.button.removeAttribute('aria-disabled');
          this.button.disabled = false;
        }
      }
      if (this.textEl && label != null) this.textEl.textContent = label;
    }

    showError(message, quiet) {
      if (this.errorEl) {
        this.errorEl.textContent = message || '';
        this.errorEl.hidden = !message;
      }
      if (message && !quiet) {
        toast(message);
        announce(message);
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.button && this.button.disabled) return;

      // Never send more than is still addable: clamp and explain instead.
      const qtyInput = this.qtyInput();
      const remaining = remainingStock(qtyInput, this.currentVariantId());
      if (isFinite(remaining)) {
        const wanted = parseInt(qtyInput.value, 10) || 1;
        if (remaining <= 0) {
          this.syncStock();
          return;
        }
        if (wanted > remaining) {
          qtyInput.value = String(remaining);
          const stepper = qtyInput.closest('quantity-input');
          if (stepper && typeof stepper.syncButtons === 'function') stepper.syncButtons();
          this.showError(t('quantityError', '').replace('[qty]', String(remaining)));
          return;
        }
      }

      const original = this.textEl ? this.textEl.textContent : '';
      this.showError('');
      this.setBusy(true, t('adding', 'Adding…'));

      try {
        await cart.add(new FormData(this.form));
        if (this.closest('#QuickViewModal')) {
          DrawerController.closeDrawer('QuickViewModal');
          return;
        }

        /* Confirm in place before resetting. Previously the label snapped
           straight back to "Add to cart", which on a fast connection is
           indistinguishable from nothing having happened — the single most
           common reason a shopper adds the same item twice. The button holds
           a success state briefly, then returns to rest. */
        this.setBusy(false, t('added', 'Added'));
        if (this.button) {
          this.button.classList.add('is-added');
          this.button.setAttribute('aria-disabled', 'true');
          window.clearTimeout(this.resetTimer);
          this.resetTimer = window.setTimeout(() => {
            if (!this.isConnected || !this.button) return;
            this.button.classList.remove('is-added');
            /* Defer to current state rather than the snapshot taken before the
               request. Switching to a sold-out variant within the 1.8s window
               used to leave a button reading "Add to cart", still `disabled`,
               with aria-disabled stripped — dead to clicks and lying to AT. */
            if (this.button.disabled) {
              if (this.button.dataset.soldOut === 'true' && this.textEl) {
                this.textEl.textContent = t('soldOut', 'Sold out');
              }
              return;
            }
            this.button.removeAttribute('aria-disabled');
            if (this.textEl) this.textEl.textContent = original || t('addToCart', 'Add to cart');
          }, 1800);
        }
      } catch (error) {
        this.setBusy(false, original || t('addToCart', 'Add to cart'));
        if (error && error.status === 422) {
          // The cart and the stock disagreed (another tab, or stock changed
          // since the page loaded): re-read the cart and clamp to the truth.
          const fresh = await cart.get().catch(() => null);
          if (fresh) updateCartCounts(fresh);
          this.syncStock(fresh);
          const left = remainingStock(this.qtyInput(), this.currentVariantId(), fresh);
          if (isFinite(left) && left > 0) this.showError(t('quantityError', '').replace('[qty]', String(left)));
          else this.showError(t('maxInCart', ''));
        } else {
          const message = error && error.message && !/^Request failed/.test(error.message) ? error.message : t('unavailable', 'Unavailable');
          this.showError(message);
        }
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.resetTimer);
      if (this.onCartUpdate) document.removeEventListener('cart:updated', this.onCartUpdate);
    }
  }

  class ProductFormQuick extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form') || this.querySelector('[data-quick-add-form]');
      if (!this.form) return;
      this.button =
        this.querySelector('[data-quick-add-button]') || this.querySelector('button[type="submit"]');
      this.textEl = this.querySelector('[data-quick-add-text]') || this.button;
      this.addLabel = this.textEl ? this.textEl.textContent : '';
      this.originalTitle = this.button ? this.button.getAttribute('title') : null;
      this.originalAriaLabel = this.button ? this.button.getAttribute('aria-label') : null;

      on(this.form, 'submit', this.onSubmit.bind(this));
      this.onCartUpdate = (event) => this.syncStock(event && event.detail && event.detail.cart);
      on(document, 'cart:updated', this.onCartUpdate);
      this.syncStock();
    }

    disconnectedCallback() {
      if (this.onCartUpdate) document.removeEventListener('cart:updated', this.onCartUpdate);
    }

    syncStock(freshCart) {
      if (!this.button) return;
      const idInput = this.form.querySelector('[name="id"]');
      const remaining = remainingStock(this.form, idInput && idInput.value, freshCart);
      if (!isFinite(remaining)) return;
      if (remaining <= 0) {
        this.stockBlocked = true;
        this.button.disabled = true;
        this.button.setAttribute('aria-disabled', 'true');
        this.button.setAttribute('aria-label', t('maxInCart', ''));
        this.button.title = t('maxInCart', '');
        if (!this.button.classList.contains('is-added') && this.textEl) {
          this.textEl.textContent = t('allInCartShort', t('maxInCart', ''));
        }
      } else if (this.stockBlocked) {
        this.stockBlocked = false;
        this.button.disabled = false;
        this.button.removeAttribute('aria-disabled');
        if (this.originalAriaLabel == null) this.button.removeAttribute('aria-label');
        else this.button.setAttribute('aria-label', this.originalAriaLabel);
        if (this.originalTitle == null) this.button.removeAttribute('title');
        else this.button.setAttribute('title', this.originalTitle);
        if (this.textEl) this.textEl.textContent = this.addLabel;
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.button || this.button.disabled) return;

      // Cards add one unit; skip the request when the cart already holds all stock.
      const idInput = this.form.querySelector('[name="id"]');
      if (remainingStock(this.form, idInput && idInput.value) <= 0) {
        const full = t('maxInCart', '');
        toast(full);
        announce(full);
        return;
      }

      const original = this.textEl ? this.textEl.textContent : '';
      this.button.disabled = true;
      this.button.classList.add('is-loading');
      if (this.textEl) this.textEl.textContent = t('adding', 'Adding…');

      try {
        await cart.add(new FormData(this.form));
        this.button.classList.remove('is-loading');
        this.button.classList.add('is-added');
        if (this.textEl) this.textEl.textContent = t('added', 'Added');
        // Brief confirmation state, then return to the resting label.
        window.setTimeout(() => {
          this.button.classList.remove('is-added');
          this.button.disabled = false;
          if (this.textEl) this.textEl.textContent = original;
          this.syncStock();
        }, 1600);
      } catch (error) {
        this.button.classList.remove('is-loading');
        this.button.disabled = false;
        if (this.textEl) this.textEl.textContent = original;
        const message = error && error.status === 422
          ? t('maxInCart', '')
          : (error && error.message && !/^Request failed/.test(error.message) ? error.message : t('unavailable', 'Unavailable'));
        toast(message);
        announce(message);
        if (error && error.status === 422) {
          const fresh = await cart.get().catch(() => null);
          if (fresh) updateCartCounts(fresh);
          this.syncStock(fresh);
        }
      }
    }
  }

  /* ========================================================================
     12. <quantity-input>
     ======================================================================== */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.qty__input') || this.querySelector('input[type="number"]');
      if (!this.input) return;

      this.line = this.getAttribute('data-line');
      this.row = this.closest('.cline') || this.closest('[data-cart-line]');

      this.buttons = $$('.qty__btn', this);
      this.buttons.forEach((btn, index) => {
        const step = this.stepFor(btn, index);
        on(btn, 'click', (event) => {
          event.preventDefault();
          this.nudge(step);
        });
      });

      this.commit = debounce(this.pushToCart.bind(this), 400);
      on(this.input, 'change', () => {
        this.clamp();
        if (this.line != null) this.commit();
      });
      on(this.input, 'keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    /** +/- intent: explicit attributes first, then DOM order as a fallback. */
    stepFor(btn, index) {
      if (btn.hasAttribute('data-qty-plus') || btn.getAttribute('data-direction') === 'up') return 1;
      if (btn.hasAttribute('data-qty-minus') || btn.getAttribute('data-direction') === 'down') return -1;
      const name = (btn.getAttribute('name') || '').toLowerCase();
      if (name === 'plus' || name === 'increase') return 1;
      if (name === 'minus' || name === 'decrease') return -1;
      return index === 0 ? -1 : 1;
    }

    bounds() {
      const min = this.input.min !== '' ? Number(this.input.min) : this.line != null ? 0 : 1;
      const max = this.input.max !== '' ? Number(this.input.max) : Infinity;
      const step = Number(this.input.step) || 1;
      return { min: isFinite(min) ? min : 0, max, step };
    }

    clamp() {
      const { min, max } = this.bounds();
      let value = parseInt(this.input.value, 10);
      if (!isFinite(value)) value = min;
      const clamped = Math.min(max, Math.max(min, value));
      if (clamped !== value && isFinite(max) && value > max) {
        const message = t('quantityError').replace('[qty]', String(max));
        if (message) {
          toast(message);
          announce(message);
        }
      }
      this.input.value = String(clamped);
      this.syncButtons();
      return clamped;
    }

    syncButtons() {
      const { min, max } = this.bounds();
      const value = parseInt(this.input.value, 10) || min;
      this.buttons.forEach((btn, index) => {
        const step = this.stepFor(btn, index);
        btn.disabled = step < 0 ? value <= min : value >= max;
      });
    }

    nudge(step) {
      const { min, max, step: unit } = this.bounds();
      const current = parseInt(this.input.value, 10) || min;
      const next = Math.min(max, Math.max(min, current + step * unit));
      if (next === current) return;
      this.input.value = String(next);
      this.syncButtons();
      this.flash();
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * A one-beat nudge on the number itself, so a stepper press is legible
     * even when the value change is a single character in a quiet colour.
     * The class is removed on animationend rather than a timeout so repeated
     * presses always restart cleanly.
     */
    flash() {
      if (prefersReducedMotion() || settings.animationsEnabled === false) return;
      const stepper = this.querySelector('.qty') || this;
      stepper.classList.remove('is-changed');
      void stepper.offsetWidth;
      stepper.classList.add('is-changed');
      stepper.addEventListener(
        'animationend',
        () => stepper.classList.remove('is-changed'),
        { once: true }
      );
    }

    async pushToCart() {
      if (this.line == null) return;
      const quantity = parseInt(this.input.value, 10);
      if (!isFinite(quantity)) return;

      if (this.row) this.row.classList.add('cline--loading');
      /* Key over index — see initCartLineRemoval. The 400ms debounce makes
         this worse here: the commit can land after another row was removed
         and the indices shifted underneath it. */
      const lineKey = this.row ? this.row.getAttribute('data-line-key') : null;
      try {
        await cart.change(lineKey ? { id: lineKey, quantity } : { line: Number(this.line), quantity });
      } catch (error) {
        const message = (error && error.message) || '';
        if (message) {
          toast(message);
          announce(message);
        }
      } finally {
        if (this.row) this.row.classList.remove('cline--loading');
      }
    }
  }

  /* ========================================================================
     13. <variant-picker>
     ======================================================================== */

  /** Availability states, in the same vocabulary as `.mprod__stock--<state>`. */
  const STOCK_STATES = ['in_stock', 'low_stock', 'made_to_order', 'sold_out'];

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const script = this.querySelector('[data-variant-json]');
      this.variants = [];
      if (script) {
        try {
          const parsed = JSON.parse(script.textContent);
          this.variants = Array.isArray(parsed) ? parsed : [];
        } catch (_) {
          this.variants = [];
        }
      }
      if (!this.variants.length) return;

      this.sectionId = this.getAttribute('data-section') || '';
      this.productUrl = this.getAttribute('data-url') || '';
      this.updateUrl = this.getAttribute('data-update-url') !== 'false';

      on(this, 'change', this.onChange.bind(this));
      
      this.onCartUpdate = (event) => {
        if (event && event.detail && event.detail.cart) updateCartCounts(event.detail.cart);
        if (this.currentVariant) {
          this.updateButton(this.currentVariant);
          this.updateInventory(this.scopeRoot(), this.currentVariant);
        }
      };
      on(document, 'cart:updated', this.onCartUpdate);
      
      this.markUnavailable();

      this.currentVariant = this.findVariant(this.selectedOptions());
      if (this.currentVariant && window.HamamTheme && window.HamamTheme.cartItems && window.HamamTheme.cartItems.length > 0) {
        this.onCartUpdate();
      }
    }

    disconnectedCallback() {
      if (this.onCartUpdate) {
        document.removeEventListener('cart:updated', this.onCartUpdate);
      }
    }

    /**
     * The picker only ever writes inside its own section.
     *
     * A quick view modal renders a second, complete product UI over the page it
     * was opened from, so a document-wide `querySelector('[data-price-container]')`
     * finds the *underlying* page's container and repoints the wrong product.
     * Resolution order: the section this picker declares (`data-section`), then
     * the nearest enclosing product UI, and only then the document.
     */
    scopeRoot() {
      if (this._scope && document.contains(this._scope)) return this._scope;

      let root = null;
      if (this.sectionId) {
        try {
          root =
            document.querySelector('[data-section-id="' + this.sectionId + '"]') ||
            document.getElementById('shopify-section-' + this.sectionId);
        } catch (_) {
          root = null;
        }
        // A same-id section elsewhere in the page is not our scope.
        if (root && !root.contains(this)) root = null;
      }

      if (!root) {
        root =
          this.closest('[data-quick-view-content]') ||
          this.closest('[data-quick-view-inner]') ||
          this.closest('.shopify-section') ||
          this.closest('section');
      }

      this._scope = root || document;
      return this._scope;
    }

    /** Option groups in position order. */
    groups() {
      const explicit = $$('[data-option-group], fieldset[data-option-index]', this);
      if (explicit.length) return explicit;
      const fieldsets = $$('fieldset', this);
      if (fieldsets.length) return fieldsets;
      return $$('select[data-option-index], select[name^="options"]', this).map((s) => s.parentElement || s);
    }

    selectedOptions() {
      return this.groups().map((group) => {
        const select = group.matches('select') ? group : group.querySelector('select');
        if (select) return select.value;
        const checked = group.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant(options) {
      return (
        this.variants.find((variant) => {
          const opts = variant.options || [];
          return options.every((value, index) => value == null || opts[index] === value);
        }) || null
      );
    }

    onChange() {
      const options = this.selectedOptions();
      const variant = this.findVariant(options);
      this.currentVariant = variant;

      this.markUnavailable();
      this.updateIdInputs(variant);
      this.updateButton(variant);
      this.updateMeta(variant);
      this.swapGallery(variant);
      this.pushUrl(variant);
      this.renderPrice(variant);

      this.dispatchEvent(
        new CustomEvent('variant:change', { bubbles: true, detail: { variant, options } })
      );
    }

    /**
     * Values that cannot be reached (or are sold out) get `.is-unavailable`
     * rather than being removed: the customer keeps a complete picture of the
     * product, which is what the accessibility guidance asks for.
     */
    markUnavailable() {
      const selected = this.selectedOptions();
      this.groups().forEach((group, index) => {
        const inputs = $$('input[type="radio"]', group);
        inputs.forEach((input) => {
          const probe = selected.slice();
          probe[index] = input.value;
          const match = this.findVariant(probe);
          const unavailable = !match || match.available === false;
          input.toggleAttribute('data-unavailable', unavailable);
          input.classList.toggle('is-unavailable', unavailable);
          const label =
            group.querySelector('label[for="' + input.id + '"]') ||
            input.closest('label') ||
            input.nextElementSibling;
          if (label && label.classList) label.classList.toggle('is-unavailable', unavailable);
        });

        const select = group.matches('select') ? group : group.querySelector('select');
        if (select) {
          $$('option', select).forEach((option) => {
            const probe = selected.slice();
            probe[index] = option.value;
            const match = this.findVariant(probe);
            option.classList.toggle('is-unavailable', !match || match.available === false);
          });
        }
      });
    }

    updateIdInputs(variant) {
      const scope = this.closest('product-form') || this.scopeRoot();
      $$('input[name="id"], [data-variant-id]', scope).forEach((input) => {
        input.value = variant ? String(variant.id) : '';
        /* Liquid renders this input `disabled` when the INITIAL variant is
           sold out (so no-JS cannot post an unbuyable id). Nothing used to
           clear it, and a disabled control is excluded from FormData — so
           landing on ?variant=<sold-out>, then picking an in-stock variant,
           gave an enabled button whose every submit failed with a bare
           "Unavailable". Ownership of the flag belongs here, with the value. */
        if ('disabled' in input) input.disabled = !variant;
      });
    }

    updateButton(variant) {
      const scope = this.closest('product-form') || this.scopeRoot();
      
      let availableToBuy = variant ? variant.available !== false : false;
      if (variant && variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny' && variant.inventory_quantity > 0) {
        const cartQty = cartQtyFor(variant.id);
        if (cartQty >= variant.inventory_quantity) {
          availableToBuy = false;
        }
      }

      const dynamicButton = scope.querySelector('.mprod__dynamic') || scope.querySelector('.shopify-payment-button');
      if (dynamicButton) {
        dynamicButton.style.display = (variant && availableToBuy) ? '' : 'none';
      }

      const button = scope.querySelector('[data-add-button]') || scope.querySelector('button[type="submit"]');
      if (!button) return;
      const textEl = scope.querySelector('[data-add-button-text]') || button;

      if (!variant) {
        button.disabled = true;
        button.dataset.soldOut = 'true';
        textEl.textContent = t('unavailable', 'Unavailable');
      } else if (!availableToBuy) {
        button.disabled = true;
        button.dataset.soldOut = 'true';
        const allInCart = variant.available !== false &&
          variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny' &&
          cartQtyFor(variant.id) >= variant.inventory_quantity;
        textEl.textContent = allInCart ? t('allInCartShort', t('maxInCart', '')) : t('soldOut', 'Sold out');
      } else {
        button.disabled = false;
        button.removeAttribute('data-sold-out');
        textEl.textContent = t('addToCart', 'Add to cart');
      }
      button.toggleAttribute('aria-disabled', button.disabled);
    }

    updateMeta(variant) {
      const scope = this.scopeRoot();
      const skuEl = scope.querySelector('[data-sku]');
      if (skuEl) {
        const sku = variant && variant.sku ? variant.sku : '';
        const valueEl = skuEl.querySelector('[data-sku-value]') || skuEl;
        valueEl.textContent = sku;
        skuEl.hidden = !sku;
      }

      this.updateInventory(scope, variant);
      this.updateBatchInfo(scope, variant);
    }

    /**
     * Availability state, mirroring the Liquid in main-product.liquid's
     * `availability` block one branch for one branch. Keep the two in step.
     */
    stockState(variant, threshold) {
      if (!variant || variant.available === false) return 'sold_out';
      
      if (variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny' && variant.inventory_quantity > 0) {
        const cartQty = cartQtyFor(variant.id);
        if (cartQty >= variant.inventory_quantity) return 'sold_out';
      }

      if (variant.inventory_management == null) return 'in_stock';
      const qty = Number(variant.inventory_quantity) - (
        variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny'
          ? cartQtyFor(variant.id) : 0
      );
      if (!isFinite(qty) || qty <= 0) return 'made_to_order';
      if (qty <= threshold) return 'low_stock';
      return 'in_stock';
    }

    stockText(state, variant, showCount) {
      const limited = variant && variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny' && variant.inventory_quantity > 0;
      const inCart = limited ? cartQtyFor(variant.id) : 0;
      const qty = Number(variant && variant.inventory_quantity) - inCart;
      const tracked = variant && variant.inventory_management != null;
      const withCount = (key) => t(key, '').replace('[count]', String(qty));

      if (state === 'sold_out') return limited && inCart > 0 ? t('maxInCart', '') : t('soldOutText', t('soldOut', ''));
      if (limited && inCart > 0) return withCount('remainingToAdd');
      if (state === 'low_stock') return withCount('lowStock');
      if (state === 'made_to_order') return t('madeToOrder', '');
      if (showCount && tracked && isFinite(qty) && qty > 0) return withCount('inStockCount');
      return t('inStock', '');
    }

    /**
     * Rewrites the visible stock line, not just its data attributes.
     *
     * Updating `data-inventory-quantity` alone left the previous variant's
     * wording on screen — a sold-out variant kept reading "In stock". The label
     * lives in its own [data-inventory-text] span so the status dot survives.
     */
    updateInventory(scope, variant) {
      const qtyInput = scope.querySelector('.qty__input') || scope.querySelector('input[type="number"]');
      if (qtyInput && variant) {
        if (variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny' && variant.inventory_quantity > 0) {
          const cartQty = cartQtyFor(variant.id);
          const remaining = Math.max(0, variant.inventory_quantity - cartQty);
          qtyInput.max = remaining;
          if (Number(qtyInput.value) > remaining) {
            qtyInput.value = remaining || 1;
            qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else {
          qtyInput.removeAttribute('max');
        }
      }

      const invEl = scope.querySelector('[data-inventory]');
      if (!invEl || !variant) return;

      const parsed = Number(invEl.getAttribute('data-low-stock-threshold'));
      const threshold = isFinite(parsed) && parsed > 0 ? parsed : 5;
      const showCount = invEl.getAttribute('data-show-count') !== 'false';

      invEl.setAttribute('data-inventory-quantity', String(variant.inventory_quantity ?? ''));
      invEl.toggleAttribute('data-inventory-limited', variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny' && variant.inventory_quantity > 0);
      invEl.toggleAttribute('data-available', variant.available !== false);

      const state = this.stockState(variant, threshold);
      STOCK_STATES.forEach((name) => {
        invEl.classList.toggle('mprod__stock--' + name, name === state);
      });

      const textEl = invEl.querySelector('[data-inventory-text]') || invEl;
      const message = this.stockText(state, variant, showCount);
      if (message) textEl.textContent = message;
    }

    /**
     * Expiry date + batch number. Both values, the state and the days remaining
     * are pre-computed in Liquid and carried in the variant payload, so the
     * client never re-derives a date and can never disagree with the server.
     */
    /**
     * Batch rows: the expiry DATE and the batch number, nothing else.
     *
     * The owner asked for the date itself rather than a derived status, so
     * there is deliberately no "expires in N days" / "past expiry" wording and
     * no state attribute here. `variant.expiry` arrives pre-formatted from
     * Liquid (e.g. 01/02/2027), which keeps the server and client identical and
     * means no date maths happens in JS at all.
     */
    updateBatchInfo(scope, variant) {
      const box = scope.querySelector('[data-batch-info]');
      if (!box || !variant) return;

      const setRow = (valueSelector, rowSelector, value) => {
        const valueEl = box.querySelector(valueSelector);
        if (!valueEl) return;
        const text = value == null ? '' : String(value);
        valueEl.textContent = text;
        const row = valueEl.closest(rowSelector) || valueEl;
        row.hidden = text === '';
      };

      setRow('[data-expiry]', '[data-expiry-row]', variant.expiry);
      setRow('[data-batch]', '[data-batch-row]', variant.batch);
    }

    swapGallery(variant) {
      const mediaId = variant && (variant.featured_media ? variant.featured_media.id : variant.featured_image && variant.featured_image.id);
      if (!mediaId) return;
      const gallery = this.scopeRoot().querySelector('product-gallery');
      // `true` is explicit rather than relying on the default, so this call
      // site states outright that changing variant must move the gallery.
      if (gallery && typeof gallery.selectMedia === 'function') gallery.selectMedia(mediaId, true);
    }

    pushUrl(variant) {
      if (!this.updateUrl || !variant || !window.history || !history.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      history.replaceState({ variant: variant.id }, '', url.toString());
    }

    /** Price + badges come back from the section so Liquid stays the source of truth. */
    async renderPrice(variant) {
      const containers = this.scopeRoot().querySelectorAll('[data-price-container]');
      if (!containers.length || !this.sectionId || !variant) return;

      const base = this.productUrl || window.location.pathname;
      const url = base + '?section_id=' + encodeURIComponent(this.sectionId) + '&variant=' + encodeURIComponent(variant.id);

      /* Every other update in onChange is synchronous and therefore always
         matches the newest selection — only the price is fetched. Clicking
         through option pills quickly could leave variant A's price on screen
         beside variant B's selected id: the shopper reads a price that is not
         the one they would be charged. */
      if (this.priceController) this.priceController.abort();
      const priceController = typeof AbortController !== 'undefined' ? new AbortController() : null;
      this.priceController = priceController;

      try {
        const doc = await fetchDom(url, priceController ? { signal: priceController.signal } : undefined);
        if (this.currentVariant !== variant) return; /* superseded */
        const sourceContainers = doc.querySelectorAll('[data-price-container]');
        containers.forEach((container, i) => {
          if (sourceContainers[i]) container.innerHTML = sourceContainers[i].innerHTML;
        });
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        // Fall back to a client-side format so the price is never stale.
        containers.forEach(container => {
          const current = container.querySelector('.price__current');
          if (current) current.textContent = formatMoney(variant.price);
        });
      }
    }
  }

  /* ========================================================================
     14. <product-gallery>
     ======================================================================== */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-gallery-rail]') || this.querySelector('.rail');
      this.slides = $$('[data-gallery-slide]', this);
      this.thumbs = $$('[data-gallery-thumb]', this);
      this.main = this.querySelector('[data-gallery-main]');

      this.thumbs.forEach((thumb) => {
        on(thumb, 'click', (event) => {
          event.preventDefault();
          this.selectMedia(thumb.getAttribute('data-media-id'), true);
        });
        on(thumb, 'keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            this.selectMedia(thumb.getAttribute('data-media-id'), true);
          }
        });
      });

      if (this.rail && this.slides.length && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                this.setActiveThumb(entry.target.getAttribute('data-media-id'));
              }
            });
          },
          { root: this.rail, threshold: [0.61] }
        );
        this.slides.forEach((slide) => this.observer.observe(slide));
      }

      if (this.hasAttribute('data-zoom')) this.initZoom();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    setActiveThumb(mediaId) {
      if (!mediaId) return;
      this.thumbs.forEach((thumb) => {
        const active = thumb.getAttribute('data-media-id') === String(mediaId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    /**
     * Show a specific media item.
     *
     * `scroll` defaults to TRUE on purpose. In the rail layout every slide is
     * `grid-auto-columns: 100%`, so which image the customer sees is decided
     * entirely by the rail's scroll position — `.is-active` is decorative. A
     * caller that omitted the flag therefore toggled a class and changed
     * nothing on screen, which is exactly how variant switching silently
     * stopped updating the image. "Select this media" should always mean
     * "make it visible"; pass `false` only to set state without moving.
     */
    selectMedia(mediaId, scroll = true) {
      if (!mediaId) return;
      const id = String(mediaId);
      this.setActiveThumb(id);

      const slide = this.slides.find((s) => s.getAttribute('data-media-id') === id);

      if (slide && this.rail) {
        if (scroll) {
          // scrollIntoView on a snap rail keeps LTR/RTL correct for free.
          slide.scrollIntoView({ behavior: scrollBehavior(), block: 'nearest', inline: 'start' });
        }
        this.slides.forEach((s) => s.classList.toggle('is-active', s === slide));
        return;
      }

      // Single-image layout: repoint the main <img> from the thumb's data.
      const thumb = this.thumbs.find((th) => th.getAttribute('data-media-id') === id);
      const img = this.main ? this.main.querySelector('img') : null;
      if (thumb && img) {
        const src = thumb.getAttribute('data-media-src') || (thumb.querySelector('img') || {}).src;
        const srcset = thumb.getAttribute('data-media-srcset');
        const alt = thumb.getAttribute('data-media-alt');
        if (src) img.src = src;
        if (srcset) img.srcset = srcset;
        else img.removeAttribute('srcset');
        if (alt != null) img.alt = alt;
      }
    }

    initZoom() {
      const zoomTargets = this.main ? [this.main] : this.slides;
      zoomTargets.forEach((target) => {
        on(target, 'click', (event) => {
          if (prefersReducedMotion()) return;
          const rect = target.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          const x = ((event.clientX - rect.left) / rect.width) * 100;
          const y = ((event.clientY - rect.top) / rect.height) * 100;
          const img = target.querySelector('img');
          if (img) img.style.transformOrigin = x + '% ' + y + '%';
          target.classList.toggle('is-zoomed');
        });
      });
    }
  }

  /* ========================================================================
     15. <rail-carousel>
     ======================================================================== */

  class RailCarousel extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-rail]') || this.querySelector('.rail');
      if (!this.rail) return;

      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');

      if (this.prev) on(this.prev, 'click', () => this.step(-1));
      if (this.next) on(this.next, 'click', () => this.step(1));

      this.onScroll = throttleRaf(this.syncButtons.bind(this));
      this.onResize = debounce(this.syncButtons.bind(this), 150);

      on(this.rail, 'scroll', this.onScroll, { passive: true });
      on(window, 'resize', this.onResize);
      this.syncButtons();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
    }

    items() {
      return Array.from(this.rail.children);
    }

    /** One item plus the grid gap. */
    stepDistance() {
      const items = this.items();
      if (!items.length) return this.rail.clientWidth;
      const width = items[0].getBoundingClientRect().width;
      const gap = parseFloat(window.getComputedStyle(this.rail).columnGap || '0') || 0;
      return Math.max(1, Math.round(width + gap));
    }

    /**
     * RTL scroll inversion.
     * `scrollBy({left})` is a *physical* axis, not a logical one, so it cannot
     * be expressed as "inline" directly. In an RTL container the inline-start
     * edge is on the right, so advancing to the next item always means moving
     * the physical scroll position *down* (current browsers report scrollLeft
     * as 0 → -max; legacy engines reported max → 0 — a negative delta is the
     * correct "next" in both models). Hence the -1 multiplier under RTL.
     */
    step(direction) {
      const delta = this.stepDistance() * direction * (Theme.isRtl ? -1 : 1);
      this.rail.scrollBy({ left: delta, behavior: scrollBehavior() });
    }

    /**
     * End detection is done from geometry, not from scrollLeft, because the
     * sign and origin of scrollLeft under RTL differ between engines while
     * getBoundingClientRect is always physical and reliable. We project each
     * rect onto a logical axis that increases in the reading direction.
     */
    syncButtons() {
      if (!this.prev && !this.next) return;
      const items = this.items();
      if (!items.length) return;

      const rtl = Theme.isRtl;
      const logicalStart = (rect) => (rtl ? -rect.right : rect.left);
      const logicalEnd = (rect) => (rtl ? -rect.left : rect.right);

      const railRect = this.rail.getBoundingClientRect();
      const firstRect = items[0].getBoundingClientRect();
      const lastRect = items[items.length - 1].getBoundingClientRect();

      const tolerance = 2;
      const scrollable = this.rail.scrollWidth - this.rail.clientWidth > tolerance;
      const atStart = !scrollable || logicalStart(firstRect) >= logicalStart(railRect) - tolerance;
      const atEnd = !scrollable || logicalEnd(lastRect) <= logicalEnd(railRect) + tolerance;

      if (this.prev) this.prev.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;
      this.classList.toggle('is-scrollable', scrollable);
    }
  }

  /* ========================================================================
     16. <predictive-search>
     ======================================================================== */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]') || this.querySelector('input[name="q"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results) return;

      this.status = this.querySelector('[data-predictive-status]');
      this.controller = null;
      this.activeIndex = -1;

      this.search = debounce(this.run.bind(this), 250);

      on(this.input, 'input', () => {
        const q = this.input.value.trim();
        if (q.length < 2) {
          this.clear();
          return;
        }
        this.setLoading();
        this.search(q);
      });

      on(this.input, 'keydown', this.onKeydown.bind(this));
      on(this, 'keydown', (event) => {
        if (event.key !== 'Escape') return;
        /* <predictive-search> wraps the WHOLE search drawer (head, close button
           and body), and stopPropagation was unconditional — so the document
           handler that closes the top drawer never ran and an aria-modal
           dialog could not be dismissed with Escape, ever. Only consume the
           key when there is actually search state to clear; otherwise let it
           bubble to DrawerController.closeTop(). */
        const hasState = Boolean(this.input && this.input.value.length) || this.hasAttribute('data-open');
        if (!hasState) return;
        event.stopPropagation();
        this.clear();
        if (this.input) {
          this.input.value = '';
          this.input.focus();
        }
      });
      on(this, 'focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.close();
      });

      /* The clear button is a native `type="reset"`: it empties the input but
         fires no `input` event, so the results would stay on screen. `reset`
         bubbles, so one listener here covers the form whatever markup wraps it.
         The input is cleared after the default reset action has run. */
      on(this, 'reset', () => {
        if (this.controller) this.controller.abort();
        window.setTimeout(() => {
          this.clear();
          if (this.input) {
            this.input.value = '';
            this.input.focus();
          }
        }, 0);
      });
    }

    items() {
      return $$('[data-search-result]', this.results);
    }

    setLoading() {
      this.setAttribute('data-loading', '');
      this.setAttribute('aria-busy', 'true');
      if (this.status) this.status.textContent = t('searching', 'Searching…');
    }

    open() {
      this.setAttribute('data-open', '');
      this.results.hidden = false;
      const combobox = this.input;
      if (combobox && combobox.hasAttribute('aria-expanded')) combobox.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.removeAttribute('data-open');
      this.results.hidden = true;
      this.activeIndex = -1;
      if (this.input && this.input.hasAttribute('aria-expanded')) this.input.setAttribute('aria-expanded', 'false');
    }

    clear() {
      this.close();
      this.removeAttribute('data-loading');
      this.removeAttribute('aria-busy');
      this.results.innerHTML = '';
      if (this.status) this.status.textContent = '';
    }

    async run(query) {
      if (!routes.predictiveSearch) return;
      if (this.controller) this.controller.abort();
      this.controller = typeof AbortController !== 'undefined' ? new AbortController() : null;

      const url =
        routes.predictiveSearch +
        '?q=' +
        encodeURIComponent(query) +
        '&resources[type]=product,collection,page,article' +
        '&resources[limit]=6' +
        '&section_id=predictive-search';

      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          signal: this.controller ? this.controller.signal : undefined
        });
        if (!response.ok) throw new Error('Search failed');
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source =
          doc.querySelector('[data-predictive-results]') ||
          doc.querySelector('#shopify-section-predictive-search') ||
          doc.body;

        this.results.innerHTML = source ? source.innerHTML : '';
        this.removeAttribute('data-loading');
        this.removeAttribute('aria-busy');
        this.activeIndex = -1;

        const count = this.items().length;
        if (!count) {
          if (this.status) this.status.textContent = t('noResults', 'No results');
          this.open();
          announce(t('noResults', 'No results'));
        } else {
          if (this.status) this.status.textContent = '';
          this.open();
          const updated = t('resultsUpdated', '');
          announce(updated ? updated + ' (' + count + ')' : String(count));
        }
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        this.removeAttribute('data-loading');
        this.removeAttribute('aria-busy');
        if (this.status) this.status.textContent = t('noResults', 'No results');
      }
    }

    highlight(index) {
      const items = this.items();
      if (!items.length) return;
      const bounded = ((index % items.length) + items.length) % items.length;
      this.activeIndex = bounded;
      items.forEach((item, i) => {
        const active = i === bounded;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) {
          if (typeof item.focus === 'function' && item.matches('a, button')) item.focus();
          else item.scrollIntoView({ block: 'nearest', behavior: scrollBehavior() });
        }
      });
    }

    onKeydown(event) {
      const items = this.items();
      switch (event.key) {
        case 'ArrowDown':
          if (!items.length) return;
          event.preventDefault();
          this.highlight(this.activeIndex + 1);
          break;
        case 'ArrowUp':
          if (!items.length) return;
          event.preventDefault();
          this.highlight(this.activeIndex - 1);
          break;
        case 'Enter': {
          if (this.activeIndex < 0 || !items.length) return; // let the form submit
          event.preventDefault();
          const item = items[this.activeIndex];
          const link = item.matches('a') ? item : item.querySelector('a');
          if (link) window.location = link.href;
          break;
        }
        default:
          break;
      }
    }
  }

  /* ========================================================================
     16b. <sticky-atc>
     ======================================================================== */

  /**
   * Mobile sticky add-to-cart bar. Watches a `[data-atc-sentinel]` sitting next
   * to the real add-to-cart button and shows itself once that sentinel has
   * scrolled off the top of the viewport — `boundingClientRect.top < 0` keeps
   * the bar hidden while the button is still below the fold.
   *
   * The sentinel is found by `data-sentinel` (an element id) when given, else
   * within the closest section. Desktop never observes: the bar is a small
   * screen affordance, so the media query is a hard gate rather than CSS-only.
   */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      if (!('IntersectionObserver' in window)) return;

      const desktop =
        typeof window.matchMedia === 'function' ? window.matchMedia('(min-width: 990px)') : null;
      if (desktop && desktop.matches) return;

      const sentinelId = this.getAttribute('data-sentinel');
      let sentinel = sentinelId ? document.getElementById(sentinelId) : null;

      if (!sentinel) {
        const host =
          this.closest('[data-section-id]') ||
          this.closest('.shopify-section') ||
          this.closest('section') ||
          document;
        sentinel = host.querySelector('[data-atc-sentinel]');
      }
      if (!sentinel) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.classList.toggle('is-visible', visible);
            /* Mirrored onto <body> so other fixed furniture can get out of the
               way — the back-to-top button lifts above this bar rather than
               sitting on top of the primary conversion action. */
            document.body.classList.toggle('has-sticky-atc', visible);
          });
        },
        { threshold: 0 }
      );
      this.observer.observe(sentinel);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.body.classList.remove('has-sticky-atc');
    }
  }

  /* ========================================================================
     17. <facet-filters>
     ======================================================================== */

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form') || this.querySelector('[data-facet-form]');
      this.onPopState = this.restore.bind(this);

      if (this.form) {
        on(this.form, 'input', this.onInput.bind(this));
        on(this.form, 'change', this.onChange.bind(this));
        on(this.form, 'submit', (event) => {
          event.preventDefault();
          this.apply(this.buildUrl());
        });
      }

      /* Named + stored so disconnectedCallback can actually remove it. As an
         anonymous closure this leaked one live listener per section reload in
         the theme editor, and each detached instance still targeted the live
         grid — so "Clear all" fired N duplicate requests and N history entries. */
      this.onDocumentClick = (event) => {
        const clear = event.target instanceof Element ? event.target.closest('[data-facet-clear]') : null;
        if (!clear) return;
        event.preventDefault();
        this.apply(clear.getAttribute('href') || window.location.pathname);
      };
      on(document, 'click', this.onDocumentClick);

      on(window, 'popstate', this.onPopState);

      this.debouncedPrice = debounce(() => this.apply(this.buildUrl()), 500);
      this.initViewToggle();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
      if (this.controller) this.controller.abort();
    }

    onInput(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      // Price range typing/dragging is noisy — hold off until it settles.
      if (target.matches('[data-facet-price], input[type="range"], input[name*="price"]')) {
        this.debouncedPrice();
      }
    }

    onChange(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.matches('[data-facet-price], input[type="range"], input[name*="price"]')) return;
      this.apply(this.buildUrl());
    }

    buildUrl() {
      if (!this.form) return window.location.href;
      const data = new FormData(this.form);
      const params = new URLSearchParams();
      data.forEach((value, key) => {
        if (String(value).length) params.append(key, value);
      });
      const action = this.form.getAttribute('action') || window.location.pathname;
      const base = action.split('?')[0];
      const query = params.toString();
      return query ? base + '?' + query : base;
    }

    async apply(url, options) {
      const opts = options || {};
      /* Sequencing. onChange fires per checkbox with no debounce, so N rapid
         ticks put N fetches in flight; without this the LAST-RESOLVING (not the
         newest) response won the grid, and pushState ran once per response in
         completion order — leaving the URL, the checkboxes and the results
         disagreeing, and Back needing several presses.
         <predictive-search> already does exactly this; the pattern just was not
         carried across. */
      if (this.controller) this.controller.abort();
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      this.controller = controller;
      const token = (this.token = (this.token || 0) + 1);
      this.setAttribute('aria-busy', 'true');
      this.classList.add('is-loading');

      /* Dim the results that are already on screen rather than replacing them
         with skeletons. A skeleton is the right answer when there is nothing
         to show yet; here there is a full grid of real products, and tearing
         it out to display placeholder boxes of a different height collapses
         the page and throws the shopper's scroll position away. Holding the
         old grid in place, greyed, keeps every dimension stable through the
         swap. */
      const gridRegion = document.querySelector('[data-product-grid-container]');
      if (gridRegion) gridRegion.classList.add('is-loading-region');

      try {
        const doc = await fetchDom(url, controller ? { signal: controller.signal } : undefined);
        if (token !== this.token) return; /* superseded by a newer selection */
        ['[data-product-grid-container]', '[data-active-filters]', '[data-product-count]'].forEach((selector) => {
          const source = doc.querySelector(selector);
          const target = document.querySelector(selector);
          if (source && target) target.innerHTML = source.innerHTML;
        });

        // Refresh the facet form itself so counts and disabled states track.
        const freshForm = doc.querySelector('[data-facet-form]') || (doc.querySelector('facet-filters') || {}).firstElementChild;
        if (freshForm && this.form && freshForm.tagName === this.form.tagName && !opts.keepForm) {
          const focusedName = document.activeElement instanceof Element ? document.activeElement.getAttribute('name') : null;
          this.form.innerHTML = freshForm.innerHTML;
          if (focusedName) {
            const restored = this.form.querySelector('[name="' + focusedName + '"]');
            if (restored) restored.focus({ preventScroll: true });
          }
        }

        if (!opts.fromPopState && window.history && history.pushState) {
          history.pushState({ facets: url }, '', url);
        }

        this.applyStoredView();
        announce(t('resultsUpdated', ''));

        const grid = document.querySelector('[data-product-grid-container]');
        if (grid && !opts.fromPopState) {
          const top = grid.getBoundingClientRect().top + window.scrollY - 120;
          window.scrollTo({ top: Math.max(0, top), behavior: scrollBehavior() });
        }

        /* Fresh results arrive already revealed. Running the scroll-reveal
           entrance over them would fade in a grid the shopper is already
           looking at, which reads as a stutter rather than an arrival — the
           reveal is for content scrolled into, not content swapped under the
           cursor. */
        if (grid) {
          $$('[data-animate]', grid).forEach((el) => {
            el.classList.add('is-visible');
            el.dataset.animateObserved = 'true';
          });
          grid.classList.add('is-swapped');
          window.setTimeout(() => grid.classList.remove('is-swapped'), 400);
        }
        initScrollReveal();
      } catch (error) {
        if (error && error.name === 'AbortError') return; /* we cancelled it */
        window.location = url; // hard navigation beats a dead-end
      } finally {
        if (token === this.token) {
          this.removeAttribute('aria-busy');
          this.classList.remove('is-loading');
          if (gridRegion) gridRegion.classList.remove('is-loading-region');
        }
      }
    }

    restore() {
      this.apply(window.location.href, { fromPopState: true });
    }

    /* ---- Grid / list view toggle, persisted per browser ---- */

    initViewToggle() {
      this.viewButtons = $$('[data-view-toggle]');
      if (!this.viewButtons.length) {
        this.applyStoredView();
        return;
      }
      this.viewButtons.forEach((btn) => {
        on(btn, 'click', (event) => {
          event.preventDefault();
          const view = btn.getAttribute('data-view-toggle') || 'grid';
          try {
            window.localStorage.setItem('hamam:view', view);
          } catch (_) {
            /* private mode */
          }
          this.applyStoredView();
        });
      });
      this.applyStoredView();
    }

    applyStoredView() {
      let view = 'grid';
      try {
        view = window.localStorage.getItem('hamam:view') || 'grid';
      } catch (_) {
        view = 'grid';
      }
      $$('[data-view-toggle]').forEach((btn) => {
        const active = (btn.getAttribute('data-view-toggle') || 'grid') === view;
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        btn.classList.toggle('is-active', active);
      });
      const grid = document.querySelector('[data-product-grid]') || document.querySelector('.product-grid');
      if (grid) grid.classList.toggle('product-grid--list', view === 'list');
    }
  }

  /* ========================================================================
     18. Wishlist (localStorage backed)
     ======================================================================== */

  const WISHLIST_KEY = 'hamam:wishlist';

  const wishlist = {
    read() {
      try {
        const raw = window.localStorage.getItem(WISHLIST_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter((item) => item && item.id) : [];
      } catch (_) {
        return [];
      }
    },

    write(items) {
      try {
        window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
      } catch (_) {
        /* storage unavailable — the session still works, it just won't persist */
      }
      document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
      return items;
    },

    has(id) {
      const key = String(id);
      return this.read().some((item) => String(item.id) === key);
    },

    add(id, handle) {
      if (this.has(id)) return this.read();
      const items = this.read().concat([{ id: String(id), handle: handle || '' }]);
      return this.write(items);
    },

    remove(id) {
      const key = String(id);
      return this.write(this.read().filter((item) => String(item.id) !== key));
    },

    toggle(id, handle) {
      const added = !this.has(id);
      if (added) this.add(id, handle);
      else this.remove(id);
      return added;
    },

    count() {
      return this.read().length;
    }
  };

  function reflectWishlistButton(button) {
    const id = button.getAttribute('data-product-id');
    if (!id) return;
    const active = wishlist.has(id);
    const label = active ? t('wishlistRemove', 'Remove from wishlist') : t('wishlistAdd', 'Add to wishlist');

    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.setAttribute('aria-label', label);
    button.setAttribute('title', label);

    const iconOn = button.querySelector('[data-wishlist-icon-on]');
    const iconOff = button.querySelector('[data-wishlist-icon-off]');
    if (iconOn) iconOn.hidden = !active;
    if (iconOff) iconOff.hidden = active;
  }

  function reflectWishlist() {
    $$('[data-wishlist-toggle]').forEach(reflectWishlistButton);
    const count = wishlist.count();
    $$('[data-wishlist-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
  }

  function initWishlist() {
    if (settings.wishlistEnabled === false) return;

    // Delegated so cards injected by facets / quick view are covered.
    if (!initWishlist.bound) {
      on(document, 'click', (event) => {
        const button = event.target instanceof Element ? event.target.closest('[data-wishlist-toggle]') : null;
        if (!button) return;
        event.preventDefault();
        event.stopPropagation();
        const id = button.getAttribute('data-product-id');
        const handle = button.getAttribute('data-product-handle');
        if (!id) return;
        const added = wishlist.toggle(id, handle);
        reflectWishlist();
        /* The beat plays on the click only, never on reflect — otherwise every
           re-render of an already-saved card (facet update, quick view close)
           would replay it and the page would twitch. */
        if (added && !prefersReducedMotion()) {
          button.classList.add('is-just-saved');
          window.setTimeout(() => button.classList.remove('is-just-saved'), 500);
        }
        announce(added ? t('wishlistAdd', '') : t('wishlistRemove', ''));
      });
      /* Clear all. One write, so a single `wishlist:updated` re-renders the page
         and the header count instead of one event per removed item. */
      on(document, 'click', (event) => {
        const button = event.target instanceof Element ? event.target.closest('[data-wishlist-clear]') : null;
        if (!button) return;
        event.preventDefault();
        if (!wishlist.count()) return;
        wishlist.write([]);
        announce(t('wishlistCleared', t('wishlistRemove', '')));
      });
      on(document, 'wishlist:updated', () => {
        reflectWishlist();
        renderWishlistPage();
      });
      on(window, 'storage', (event) => {
        if (event.key === WISHLIST_KEY) reflectWishlist();
      });
      initWishlist.bound = true;
    }

    reflectWishlist();
    renderWishlistPage();
  }

  /** Renders saved products into a wishlist page, one card fragment per handle. */
  let wishlistRenderToken = 0;

  async function renderWishlistPage() {
    const container = document.querySelector('[data-wishlist-page]');
    if (!container) return;
    const renderToken = ++wishlistRenderToken;

    const items = wishlist.read();
    const empty = document.querySelector('[data-wishlist-empty]');

    if (!items.length) {
      container.innerHTML = '';
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;
    container.setAttribute('aria-busy', 'true');

    const cards = await Promise.all(
      items.map(async (item) => {
        if (!item.handle) return null;
        try {
          const doc = await fetchDom(rootPath('products/' + item.handle) + '?section_id=product-card-fragment');
          const card = doc.querySelector('[data-product-card]');
          return card ? card.outerHTML : null;
        } catch (_) {
          return null; // missing section or unpublished product — skip quietly
        }
      })
    );

    if (renderToken !== wishlistRenderToken) return; /* superseded by a newer toggle */
    const html = cards.filter(Boolean).join('');
    if (html) {
      container.innerHTML = html;
    } else if (!container.children.length && empty) {
      // Section fragment unavailable: degrade to whatever the server rendered.
      empty.hidden = false;
    }

    container.removeAttribute('aria-busy');
    reflectWishlist();
    initScrollReveal();
  }

  /* ========================================================================
     19. Quick view
     ======================================================================== */

  let quickViewToken = 0;

  function initQuickView() {
    if (initQuickView.bound) return;
    initQuickView.bound = true;

    /* Clear the modal when it closes. Without this the previous product's
       markup — including a live <product-form> with a bound submit listener
       and a <variant-picker> — stays mounted indefinitely and flashes on the
       next open before the new fetch lands. */
    on(document, 'drawer:close', (event) => {
      if (!event.detail || event.detail.id !== 'QuickViewModal') return;
      const host = document.querySelector('[data-quick-view-inner]');
      if (host) host.innerHTML = '';
    });

    on(document, 'click', async (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('[data-quick-view]') : null;
      if (!trigger) return;

      const modal = document.getElementById('QuickViewModal');
      const inner = modal ? modal.querySelector('[data-quick-view-inner]') : null;
      const url = trigger.getAttribute('data-product-url');
      if (!url) return;

      if (!modal || !inner) {
        window.location = url; // no modal snippet — plain navigation
        return;
      }

      event.preventDefault();
      trigger.classList.add('is-loading');
      trigger.setAttribute('aria-busy', 'true');

      /* Two rapid clicks on different cards put two fetches in flight. Without
         a token the later-RESOLVING one wins the markup while openDrawer has
         already short-circuited on the second call — so the modal could show a
         product the shopper did not click last. */
      const token = ++quickViewToken;

      try {
        const doc = await fetchDom(url + '?section_id=quick-view');
        if (token !== quickViewToken) return; /* a newer click owns the modal */
        const source = doc.querySelector('[data-quick-view-content]') || doc.querySelector('.shopify-section');
        if (!source) throw new Error('No quick view section');
        // Injected custom elements upgrade themselves on insertion.
        inner.innerHTML = source.innerHTML;
        DrawerController.openDrawer('QuickViewModal', trigger);
      } catch (_) {
        window.location = url;
      } finally {
        trigger.classList.remove('is-loading');
        trigger.removeAttribute('aria-busy');
      }
    });
  }

  /* ========================================================================
     20. Scroll reveal
     ======================================================================== */

  let revealObserver = null;

  function initScrollReveal() {
    if (settings.animationsEnabled === false || prefersReducedMotion()) return;
    if (!('IntersectionObserver' in window)) {
      $$('[data-animate]').forEach((el) => el.classList.add('is-visible'));
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target); // reveal once, then stop watching
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -5% 0px' }
      );
    }

    $$('[data-animate]:not(.is-visible)').forEach((el) => {
      if (el.dataset.animateObserved === 'true') return;
      el.dataset.animateObserved = 'true';
      revealObserver.observe(el);
    });
  }

  /* ========================================================================
     20b. Back to top
     ======================================================================== */

  /**
   * Reveals a return-to-top control once the user is far enough down that
   * scrolling back is a real chore, and only on pages long enough to warrant
   * it at all — on a short page the button is clutter.
   *
   * Visibility is driven by an IntersectionObserver watching a sentinel near
   * the top of the document rather than by a scroll listener. A scroll handler
   * here would fire hundreds of times per gesture for a single boolean; the
   * observer fires twice, when the answer actually changes.
   */
  function initBackToTop() {
    const button = document.querySelector('[data-back-to-top]');
    if (!button || initBackToTop.bound) return;

    const MIN_PAGE_HEIGHT = 2.2; // viewports
    const sentinel = document.querySelector('[data-back-to-top-sentinel]');

    const evaluate = () => {
      const worthIt =
        document.documentElement.scrollHeight > window.innerHeight * MIN_PAGE_HEIGHT;
      if (!worthIt) button.removeAttribute('data-visible');
      return worthIt;
    };

    if (sentinel && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Sentinel out of view === scrolled past it.
            if (!entry.isIntersecting && evaluate()) button.setAttribute('data-visible', '');
            else button.removeAttribute('data-visible');
          });
        },
        { threshold: 0 }
      );
      observer.observe(sentinel);
    } else {
      // No observer: fall back to a rAF-throttled scroll read.
      const onScroll = throttleRaf(() => {
        if (window.scrollY > window.innerHeight * 1.2 && evaluate()) {
          button.setAttribute('data-visible', '');
        } else {
          button.removeAttribute('data-visible');
        }
      });
      on(window, 'scroll', onScroll, { passive: true });
    }

    on(button, 'click', () => {
      window.scrollTo({ top: 0, behavior: scrollBehavior() });
      /* Keep keyboard focus at the top after the smooth scroll completes. */
      const target = document.getElementById('top') || document.body;
      window.setTimeout(() => {
        if (typeof target.focus === 'function') target.focus({ preventScroll: true });
      }, 320);
    });

    initBackToTop.bound = true;
  }

  /* ========================================================================
     21. Misc wiring
     ======================================================================== */

  /**
   * Password reveal toggles on the four account pages.
   *
   * The buttons are rendered with `hidden` and un-hidden here, so with
   * JavaScript unavailable no dead control appears — the field simply stays
   * masked, which is the correct degradation for a password input.
   *
   * Delegated from the document rather than bound per button: the theme editor
   * re-renders these sections without a page load, and a per-element listener
   * would be discarded with the replaced markup.
   */
  function initPasswordToggle() {
    $$('[data-password-toggle]').forEach((btn) => btn.removeAttribute('hidden'));

    if (initPasswordToggle.bound) return;

    on(document, 'click', (event) => {
      const btn =
        event.target instanceof Element ? event.target.closest('[data-password-toggle]') : null;
      if (!btn) return;

      const input = document.getElementById(btn.getAttribute('data-password-toggle'));
      if (!input) return;

      const reveal = btn.getAttribute('aria-pressed') !== 'true';
      input.type = reveal ? 'text' : 'password';
      btn.setAttribute('aria-pressed', reveal ? 'true' : 'false');

      /* The accessible name has to change with the state — a button that keeps
         announcing "Show password" while the password is already visible is
         actively misleading to a screen-reader user. */
      const label = reveal
        ? btn.getAttribute('data-label-hide')
        : btn.getAttribute('data-label-show');
      if (label) btn.setAttribute('aria-label', label);

      /* Keep the caret where the customer left it. Reassigning `type` moves
         the caret to position 0 in every browser, so a reveal mid-typing would
         otherwise send the next keystroke to the front of the string. */
      if (typeof input.selectionStart === 'number') {
        const end = input.value.length;
        try {
          input.setSelectionRange(end, end);
        } catch (_) {
          /* Some input types reject setSelectionRange — harmless. */
        }
      }
      input.focus({ preventScroll: true });
    });

    initPasswordToggle.bound = true;
  }

  /**
   * Cart note — delegated.
   *
   * Per-element binding does not survive a drawer swap: the textarea the
   * listener was attached to is discarded with the old innerHTML. Delegating on
   * the document means the note works in the drawer, on the cart page, and in
   * any markup section rendering injects later.
   *
   * `data-note-bound` is stamped on each field the first time it is handled,
   * because the inline fallbacks in the Liquid layer skip flagged elements.
   */
  function initCartNote() {
    if (initCartNote.bound) return;
    initCartNote.bound = true;

    const push = debounce((value) => {
      cart.updateNote(value).catch(() => {});
    }, 600);

    on(document, 'input', (event) => {
      const field = event.target instanceof Element ? event.target.closest('[data-cart-note]') : null;
      if (!field) return;
      field.dataset.noteBound = 'true';
      push(field.value);
    });
  }

  /**
   * Discount code — delegated.
   *
   * Two markup shapes exist: a real `[data-discount-form]` (cart drawer, submit
   * button) and a plain `[data-discount-field]` block (cart page, `type="button"`
   * so it never posts the enclosing cart form). Both are covered, plus Enter
   * inside the non-form field, which has no submit event to hook.
   */
  function initDiscountForm() {
    if (initDiscountForm.bound) return;
    initDiscountForm.bound = true;

    const markBound = (...elements) => {
      elements.forEach((el) => {
        if (el && el.dataset) el.dataset.discountBound = 'true';
      });
    };

    const codeIn = (scope) => {
      if (!scope || typeof scope.querySelector !== 'function') return '';
      const input = scope.querySelector('input[name="discount"]') || scope.querySelector('input');
      return input ? input.value.trim() : '';
    };

    const apply = (scope) => {
      const code = codeIn(scope);
      if (code) cart.applyDiscount(code);
    };

    on(document, 'click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-discount-apply]') : null;
      if (!button) return;

      const form = button.closest('[data-discount-form]');
      // A submit button inside a real form is handled by the submit listener,
      // so the code is not applied twice for one click.
      if (form && button.type === 'submit') {
        markBound(button, form);
        return;
      }

      event.preventDefault();
      const scope = form || button.closest('[data-discount-field]') || button.closest('form');
      markBound(button, scope);
      apply(scope);
    });

    on(document, 'submit', (event) => {
      const form = event.target instanceof Element ? event.target.closest('[data-discount-form]') : null;
      if (!form) return;
      event.preventDefault();
      markBound(form);
      apply(form);
    });

    on(document, 'keydown', (event) => {
      if (event.key !== 'Enter') return;
      const target = event.target;
      if (!(target instanceof Element) || !target.matches('input')) return;
      const field = target.closest('[data-discount-field]:not([data-discount-form])');
      if (!field) return;
      event.preventDefault();
      markBound(field);
      apply(field);
    });
  }

  function initCopyLinks() {
    on(document, 'click', async (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-copy-link]') : null;
      if (!button) return;
      event.preventDefault();
      const value = button.getAttribute('data-copy-link') || window.location.href;
      try {
        await navigator.clipboard.writeText(value);
        toast(t('copied', ''));
        announce(t('copied', ''));
      } catch (_) {
        /* clipboard blocked — nothing useful to say */
      }
    });
  }

  function initCartLineRemoval() {
    on(document, 'click', async (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-cart-remove]') : null;
      if (!button) return;
      event.preventDefault();
      const line = button.getAttribute('data-cart-remove') || button.getAttribute('data-line');
      if (line == null) return;
      const row = button.closest('.cline') || button.closest('[data-cart-line]');
      /* Address the line by KEY where the markup offers one. `line` is a
         1-based index into the CURRENT cart, so any completed removal
         renumbers every row after it — and the DOM is only renumbered once
         the drawer re-renders. Two removals inside that window delete the
         wrong item. The key is stable across renumbering. */
      const lineKey = row ? row.getAttribute('data-line-key') : null;
      if (row) row.classList.add('cline--loading');

      /* Collapse the row while the request is in flight rather than after it
         returns. The removal is near-certain to succeed, and the alternative
         — a frozen row, a network round trip, then an instant disappearance
         when the drawer re-renders — reads as a lag followed by a glitch.
         On the rare failure the class comes straight back off below. */
      const animateOut =
        row && !prefersReducedMotion() && settings.animationsEnabled !== false;
      if (animateOut) row.classList.add('is-removing');

      try {
        await cart.change(lineKey ? { id: lineKey, quantity: 0 } : { line: Number(line), quantity: 0 });
      } catch (error) {
        if (row) {
          row.classList.remove('cline--loading');
          row.classList.remove('is-removing');
        }
        if (error && error.message) toast(error.message);
      }
    });
  }

  /* ========================================================================
     21b. <offers-slider>
     ======================================================================== */

  /**
   * Promotional slider built on a scroll-snap track (see the note at the top of
   * sections/offers-slider.liquid for why scroll-snap rather than transforms).
   *
   * This class adds autoplay, dots, arrows, a counter and keyboard support. It
   * never *positions* anything itself — every move is a scrollIntoView on the
   * target slide, so the browser owns the animation and RTL needs no special
   * casing. `inline: 'start'` is a logical value: it resolves to the right edge
   * in Arabic without a sign flip anywhere in this file.
   *
   * KNOWN TRADEOFF — wrapping from the last slide back to the first sweeps the
   * whole track rather than teleporting. True seamless looping needs cloned
   * slides and a transform track, which would mean re-implementing touch
   * momentum and RTL ordering by hand. A visible sweep back is a legible
   * motion; a broken swipe on a phone is not. The sweep is the better trade.
   */
  class OffersSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-oslide-track]');
      this.slides = $$('[data-oslide-slide]', this);
      if (!this.track || this.slides.length === 0) return;

      this.dots = $$('[data-oslide-dot]', this);
      this.toggle = this.querySelector('[data-oslide-toggle]');
      this.counter = this.querySelector('[data-oslide-current]');
      this.index = 0;
      this.paused = false;
      this.timer = null;

      /* Marks the slider as script-driven. Until this lands, the CSS keeps all
         slide copy fully visible — so a failed or blocked script leaves a
         readable promo rather than an empty banner. */
      this.classList.add('oslide--js');

      const speed = parseInt(this.dataset.autoplay, 10);
      this.speed = isFinite(speed) && speed >= 2000 ? speed : 0;
      this.style.setProperty('--oslide-speed', this.speed + 'ms');

      this.bindControls();
      this.watchSlides();
      this.setCurrent(0);

      if (this.speed) this.setupAutoplay();
    }

    disconnectedCallback() {
      this.stop();
      if (this.slideObserver) this.slideObserver.disconnect();
      if (this.viewObserver) this.viewObserver.disconnect();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    bindControls() {
      const prev = this.querySelector('[data-oslide-prev]');
      const next = this.querySelector('[data-oslide-next]');
      if (prev) on(prev, 'click', () => this.nudge(-1));
      if (next) on(next, 'click', () => this.nudge(1));

      this.dots.forEach((dot) => {
        on(dot, 'click', () => {
          const i = parseInt(dot.getAttribute('data-oslide-dot'), 10);
          if (isFinite(i)) this.goTo(i, true);
        });
      });

      if (this.toggle) {
        on(this.toggle, 'click', () => {
          this.paused = !this.paused;
          this.toggle.setAttribute('aria-pressed', this.paused ? 'true' : 'false');
          const label = this.paused
            ? this.toggle.getAttribute('data-label-play')
            : this.toggle.getAttribute('data-label-pause');
          if (label) this.toggle.setAttribute('aria-label', label);
          if (this.paused) this.stop();
          else this.start();
        });
      }

      /* Arrow keys on the focused track. The strip is natively scrollable so
         the browser already handles this — but scroll-snap can land between
         slides on a fast key repeat, so stepping explicitly is steadier. */
      on(this.track, 'keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        /* Physical keys on a logical axis: ArrowRight means "next" in English
           and "previous" in Arabic. */
        const forward = Theme.isRtl ? event.key === 'ArrowLeft' : event.key === 'ArrowRight';
        this.nudge(forward ? 1 : -1);
      });
    }

    /** Which slide is showing is read from the DOM, never bookkept separately. */
    watchSlides() {
      if (!('IntersectionObserver' in window)) return;
      this.slideObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const i = parseInt(entry.target.getAttribute('data-index'), 10);
            if (isFinite(i) && i !== this.index) this.setCurrent(i);
          });
        },
        { root: this.track, threshold: 0.6 }
      );
      this.slides.forEach((s) => this.slideObserver.observe(s));
    }

    setCurrent(i) {
      this.index = i;
      this.slides.forEach((s, n) => s.classList.toggle('is-current', n === i));
      this.dots.forEach((d, n) => {
        d.classList.toggle('is-active', n === i);
        d.setAttribute('aria-selected', n === i ? 'true' : 'false');
      });
      if (this.counter) this.counter.textContent = String(i + 1);

      /* Restart the progress fill for the new slide. Removing the class,
         forcing a reflow and re-adding it is what makes the CSS animation
         replay — without the reflow the browser coalesces both changes into
         no change at all and the bar never resets. */
      if (this.timer) {
        this.classList.remove('oslide--autoplaying');
        void this.offsetWidth;
        this.classList.add('oslide--autoplaying');
      }
    }

    goTo(i, fromUser) {
      const total = this.slides.length;
      const target = ((i % total) + total) % total;
      const slide = this.slides[target];
      if (!slide) return;

      slide.scrollIntoView({
        behavior: scrollBehavior(),
        inline: 'start',
        block: 'nearest'
      });
      this.setCurrent(target);

      /* A deliberate tap resets the dwell timer — otherwise the slide the user
         just chose can be replaced a few hundred milliseconds later, which
         reads as the control being ignored. */
      if (fromUser && this.timer) this.restart();
    }

    nudge(direction) {
      this.goTo(this.index + direction, true);
    }

    /* ---- Autoplay ------------------------------------------------------- */

    setupAutoplay() {
      /* Reduced motion suppresses autoplay outright. Moving content is the
         single clearest thing that preference is asking us not to do, and the
         slider stays fully usable by swipe, arrows and dots. */
      if (prefersReducedMotion() || settings.animationsEnabled === false) return;

      /* Only run while actually on screen. A slider three folds down should
         not be burning timers and repainting for nobody. */
      if ('IntersectionObserver' in window) {
        this.viewObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.start();
              else this.stop();
            });
          },
          { threshold: 0.35 }
        );
        this.viewObserver.observe(this);
      } else {
        this.start();
      }

      /* Pause while the pointer or keyboard focus is inside — nobody wants the
         promo they are reading, or the button they are tabbing to, to slide
         out from under them. */
      on(this, 'pointerenter', () => this.stop());
      on(this, 'pointerleave', () => this.start());
      on(this, 'focusin', () => this.stop());
      on(this, 'focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.start();
      });

      this.onVisibility = () => (document.hidden ? this.stop() : this.start());
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    start() {
      if (this.timer || this.paused || !this.speed || document.hidden) return;
      this.classList.add('oslide--autoplaying');
      this.timer = window.setInterval(() => this.goTo(this.index + 1), this.speed);
      /* Kick the progress fill for the slide already showing. */
      this.setCurrent(this.index);
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
      this.classList.remove('oslide--autoplaying');
    }

    restart() {
      this.stop();
      this.start();
    }
  }

  /* ========================================================================
     22. Registration
     ======================================================================== */

  /* ----------------------------------------------------------------------
     <announcement-bar>
     Rotates between messages and handles dismissal. The section renders every
     message server-side with only the first carrying `.is-active`, so without
     JS the bar still shows one valid message rather than collapsing to nothing.
     ---------------------------------------------------------------------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-announcement-item]'));
      this.interval = parseInt(this.dataset.rotateInterval, 10) || 0;
      this.index = Math.max(
        0,
        this.items.findIndex((el) => el.classList.contains('is-active'))
      );

      const dismiss = this.querySelector('[data-announcement-dismiss]');
      if (dismiss) {
        /* Session-scoped, not permanent: a lasting opt-out would also hide a
           threshold or promo change the merchant makes next week. */
        try {
          if (sessionStorage.getItem(this.dismissKey) === '1') {
            this.hidden = true;
            return;
          }
        } catch (e) {
          /* Private browsing can throw on sessionStorage — stay visible. */
        }
        dismiss.addEventListener('click', () => this.dismiss());
      }

      this.start();

      /* Don't rotate behind a hidden tab. */
      this.onVisibility = () => (document.hidden ? this.stop() : this.start());
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    get dismissKey() {
      return 'hamam:ann-dismissed:' + (this.dataset.announcementId || 'default');
    }

    start() {
      if (this.timer || this.items.length < 2 || this.interval < 1000) return;
      /* A silently auto-advancing banner is precisely what reduced-motion is
         asking us not to do — show the first message and leave it. */
      if (prefersReducedMotion()) return;
      this.timer = setInterval(() => this.next(), this.interval);
    }

    stop() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    next() {
      const previous = this.items[this.index];
      this.index = (this.index + 1) % this.items.length;
      const current = this.items[this.index];
      if (previous) {
        previous.classList.remove('is-active');
        /* Marks the outgoing message so it exits upward while the incoming one
           arrives from below. Without this both items animate on the same axis
           in the same direction and the rotation reads as a flicker rather
           than as one message replacing another. */
        previous.classList.add('is-leaving');
        window.setTimeout(() => previous.classList.remove('is-leaving'), 400);
      }
      if (current) current.classList.add('is-active');
    }

    dismiss() {
      this.stop();

      /* Collapse rather than vanish. The bar sits above the header, so hiding
         it instantly yanks the entire page up by its height — the animation
         here exists to make that unavoidable reflow legible, not decorative. */
      if (!prefersReducedMotion() && settings.animationsEnabled !== false) {
        this.style.setProperty('--ann-h', this.offsetHeight + 'px');
        this.classList.add('is-dismissing');
        window.setTimeout(() => {
          this.hidden = true;
          this.classList.remove('is-dismissing');
          this.style.removeProperty('--ann-h');
          // The sticky header caches its offset; tell it to re-measure.
          window.dispatchEvent(new Event('resize'));
        }, 280);
      } else {
        this.hidden = true;
      }

      try {
        sessionStorage.setItem(this.dismissKey, '1');
      } catch (e) {
        /* Already hidden for this pageview; nothing further to do. */
      }
      document.dispatchEvent(new CustomEvent('announcement:dismissed'));
    }
  }

  const ELEMENTS = [
    ['announcement-bar', AnnouncementBar],
    ['sticky-header', StickyHeader],
    ['mega-menu', MegaMenu],
    ['mobile-nav', MobileNav],
    ['localization-form', LocalizationForm],
    ['theme-toggle', ThemeToggle],
    ['cart-drawer', CartDrawer],
    ['product-form', ProductForm],
    ['product-form-quick', ProductFormQuick],
    ['quantity-input', QuantityInput],
    ['variant-picker', VariantPicker],
    ['product-gallery', ProductGallery],
    ['rail-carousel', RailCarousel],
    ['predictive-search', PredictiveSearch],
    ['sticky-atc', StickyAtc],
    ['facet-filters', FacetFilters],
    ['offers-slider', OffersSlider]
  ];

  ELEMENTS.forEach(([name, ctor]) => {
    if (!window.customElements || customElements.get(name)) return;
    try {
      customElements.define(name, ctor);
    } catch (_) {
      /* Already defined by another bundle — harmless. */
    }
  });

  /* ========================================================================
     23. Public API
     ======================================================================== */

  Object.assign(Theme, {
    announce,
    formatMoney,
    fetchDom,
    debounce,
    trapFocus,
    releaseFocus,
    lockScroll,
    unlockScroll,
    toast,
    updateShipBar,
    drawers: DrawerController,
    openDrawer: (id, opener) => DrawerController.openDrawer(id, opener),
    closeDrawer: (id) => DrawerController.closeDrawer(id),
    closeDrawers: () => DrawerController.closeAll(),
    cart,
    wishlist,
    fetchCartRecommendations,
    initScrollReveal,
    prefersReducedMotion
  });

  /* ========================================================================
     24. Boot
     ======================================================================== */

  /** Behaviours not owned by a custom element. Safe to call repeatedly. */
  function wire() {
    initWishlist();
    initQuickView();
    initScrollReveal();
    initBackToTop();
    initPasswordToggle();
    initCartNote();
    initDiscountForm();
    fetchCartRecommendations();
    cart
      .get()
      .then((freshCart) => {
        updateCartCounts(freshCart);
        syncCardStockLabels(freshCart);
      })
      .catch(() => {});
  }

  /* Cart state fan-out.

     Every mutation dispatches `cart:updated`, but the only subscriber used to
     be <cart-drawer> — which exists only when the merchant picks the "drawer"
     cart type. Two gaps followed:
       • cart_type: 'page'  -> nothing listened at all, so the header count and
         the free-shipping bar never moved after any add.
       • the /cart page     -> rows, line totals and the subtotal are rendered
         server-side and were never refreshed, so a quantity change left stale
         numbers on screen and a removed row collapsed to an invisible-but-
         space-occupying block.
     Counts are cheap and always safe to refresh. The page re-render is scoped
     to the cart section and only runs when that section is actually present,
     so it is a no-op everywhere else. */
  let cartPageToken = 0;
  async function onCartUpdatedGlobal(event) {
    const detail = (event && event.detail) || {};
    if (detail.cart) updateCartCounts(detail.cart);
    else await cart.get().then(updateCartCounts).catch(() => {});
    syncCardStockLabels(detail.cart);

    const host = document.querySelector('[data-cart-page]');
    if (!host) return;
    const sectionId = host.getAttribute('data-cart-page');
    if (!sectionId) return;
    const token = ++cartPageToken;
    try {
      const doc = await fetchDom(routes.cart + '?section_id=' + encodeURIComponent(sectionId));
      if (token !== cartPageToken) return; /* a newer mutation owns the DOM */
      const source = doc.querySelector('[data-cart-page]');
      if (source) host.innerHTML = source.innerHTML;
    } catch (_) {
      /* Leave the stale-but-readable DOM rather than blanking the cart. */
    }
  }

  function boot() {
    initDrawers();
    initCopyLinks();
    initCartLineRemoval();
    on(document, 'cart:updated', onCartUpdatedGlobal);
    wire();
  }

  if (document.readyState === 'loading') {
    on(document, 'DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: sections are re-rendered without a page load, so re-wire the
  // delegated / one-shot behaviours. Custom elements upgrade on their own.
  on(document, 'shopify:section:load', () => wire());
  on(document, 'shopify:section:unload', (event) => {
    /* Only close drawers that actually belonged to the section being removed.
       closeAll() meant deleting or reordering ANY unrelated section snapped the
       cart drawer shut mid-inspection in the theme editor. */
    Array.from(DrawerController.open).forEach((id) => {
      const el = document.getElementById(id);
      if (!el || (event.target && event.target.contains && event.target.contains(el))) {
        DrawerController.closeDrawer(id);
      }
    });
  });
  on(document, 'shopify:block:select', (event) => {
    if (event.target && typeof event.target.scrollIntoView === 'function') {
      event.target.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
    }
  });

  if (reducedMotionQuery && typeof reducedMotionQuery.addEventListener === 'function') {
    reducedMotionQuery.addEventListener('change', () => initScrollReveal());
  }
})();


